import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import { useDataSourceQuery } from '@/hooks/useDataSources'
import { useDatabasesQuery } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import type { DatabaseUser } from '@/types/compatibility'
import {
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserGroupIcon,
  LockClosedIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ArrowRightOnRectangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { useDatabaseUsersSelectionStore, type TabType } from '@/stores/databaseUsersSelectionStore'

interface Role {
  role_name: string
  with_login?: boolean
  inherit?: boolean
  createDatabase?: boolean
  createRole?: boolean
  superuser?: boolean
  replication?: boolean
  bypassRls?: boolean
  connectionLimit?: number
  validUntil?: string | null
  created_at?: string
}

interface Grant {
  privilege: string
  object_type: string
  object_name: string
  schema_name?: string
  grant_option?: boolean
  column_privileges?: {
    column_name: string
    privileges: string[]
  }[]
  source?: string
  grantor?: string
}


export function DatabaseUserManagement() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { showToast } = useToast()
  
  // State management
  const [selectedDatabase, setSelectedDatabase] = useState(searchParams.get('database') || '')
  const { getSelection, setActiveTab: setActiveTabInStore } = useDatabaseUsersSelectionStore()
  const { activeTab } = getSelection(connectionId || '')
  const setActiveTab = (tab: TabType) => setActiveTabInStore(connectionId || '', tab)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUser, setSelectedUser] = useState<DatabaseUser | null>(null)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  
  // Data state
  const [users, setUsers] = useState<DatabaseUser[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [userGrants, setUserGrants] = useState<Grant[]>([])
  const [roleGrants, setRoleGrants] = useState<Grant[]>([])
  const [isLoadingUserGrants, setIsLoadingUserGrants] = useState(false)
  const [isLoadingRoleGrants, setIsLoadingRoleGrants] = useState(false)
  
  // Modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false)
  const [showEditRoleModal, setShowEditRoleModal] = useState(false)
  const [showGrantPrivilegesModal, setShowGrantPrivilegesModal] = useState(false)
  const [showRevokePrivilegesModal, setShowRevokePrivilegesModal] = useState(false)
  const [showRemoveMembershipModal, setShowRemoveMembershipModal] = useState(false)
  const [showReassignOwnedModal, setShowReassignOwnedModal] = useState(false)
  // Track backend-provided dependency diagnostics keyed by username (PostgreSQL)
  const [deletionDepsByUser, setDeletionDepsByUser] = useState<Record<string, unknown>>({})
  // Track current user dependencies for modals
  const [currentUserDeps, setCurrentUserDeps] = useState<{
    memberOfRoles: string[]
    grantedRoles: string[]
    defaultPrivilegesCount: number
    owned: { 
      schemas: number
      relations: number // includes tables, views, materialized views, sequences
      databases: number
      functions: number
      types: number
      languages: number
      extensions: number
      collations: number
      conversions: number
      foreign_servers: number
      foreign_data_wrappers: number
    }
    ownedDetails: {
      databases: string[]
      schemas: string[]
      relations: string[]
      functions: string[]
      types: string[]
      languages: string[]
      extensions: string[]
      collations: string[]
      conversions: string[]
      foreign_servers: string[]
      foreign_data_wrappers: string[]
    }
    memberships: {
      is_member_of: string[]
      has_members: string[]
    }
    grants: {
      summary: {
        username: string
        database_type: string
        total_privileges: number
        privilege_categories: {
          read_privileges: number
          write_privileges: number
          admin_privileges: number
          inherited_privileges: number
        }
      } | null
      role_memberships: {
        role_name: string
        admin_option: boolean
        role_privileges: {
          superuser: boolean
          create_database: boolean
          create_role: boolean
        }
        source: string
      }[]
      database_privileges: {
        database_name: string
        privileges: string[]
        object_type: string
        source: string
      }[]
      schema_privileges: {
        database_name: string
        schema_name: string
        privileges: string[]
        source: string
      }[]
      table_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        privileges: string[]
        source: string
      }[]
      column_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        column_name: string
        privileges: string[]
        source: string
      }[]
      function_privileges: {
        schema_name: string
        function_name: string
        function_signature: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[]
      sequence_privileges: {
        schema_name: string
        sequence_name: string
        sequence_full_name: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[],
      effective_permissions: {
        can_read_objects: string[]
        can_write_objects: string[]
        can_create_in: string[]
        can_delete_from: string[]
        admin_objects: string[]
      }
    }
  } | null>(null)
  
  type DeleteConfirmItem =
    | { type: 'user'; item: DatabaseUser }
    | { type: 'role'; item: Role }
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<DeleteConfirmItem | null>(null)
  
  // Fetch connection details
  const { data: dataSource, isLoading: isLoadingConnection } = useDataSourceQuery(connectionId || '')
  
  // Fetch databases
  const { data: databases, isLoading: isLoadingDatabases } = useDatabasesQuery(connectionId || '')
  const databasesList = useMemo(() => (Array.isArray(databases) ? databases : []), [databases])
  
  // Determine if PostgreSQL or MySQL
  const isPostgreSQL = dataSource?.databaseType === 'PostgreSQL'
  const isMySQL = dataSource?.databaseType === 'MySQL'
  
  // Update URL when database changes
  const handleDatabaseChange = useCallback((database: string) => {
    setSelectedDatabase(database)
    setSearchParams({ database })
    setSelectedUser(null)
    setSelectedRole(null)
    setUserGrants([])
    setRoleGrants([])
  }, [setSearchParams])
  
  // Auto-select first database if none selected
  useEffect(() => {
    if (!selectedDatabase && databasesList.length > 0 && !isLoadingDatabases) {
      const firstDbName = databasesList[0].name
      if (firstDbName) {
        handleDatabaseChange(firstDbName)
      }
    }
  }, [databasesList, selectedDatabase, isLoadingDatabases, handleDatabaseChange])

  // Load users
  const loadUsers = useCallback(async () => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoading(true)
    try {
      const response = await AgentProxyService.getDatabaseUsers(connectionId, selectedDatabase)
      console.log('Users response:', response) // Debug log
      
      // Check both possible response structures
      let usersList: DatabaseUser[] = []
      if (response && typeof response === 'object') {
        if ('users' in response) {
          usersList = (response as { users?: DatabaseUser[] }).users ?? []
        } else if ('result' in response && (response as { result?: { users?: DatabaseUser[] } }).result?.users) {
          usersList = (response as { result?: { users?: DatabaseUser[] } }).result?.users ?? []
        } else if ('data' in response && (response as { data?: { users?: DatabaseUser[] } }).data?.users) {
          usersList = (response as { data?: { users?: DatabaseUser[] } }).data?.users ?? []
        } else if (Array.isArray(response)) {
          usersList = response as DatabaseUser[]
        }
      }
      
      setUsers(usersList)
      if (usersList.length === 0) {
        console.warn('No users found in response:', response)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      showToast('error', 'Failed to load users', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, showToast])
  
  // Load roles (PostgreSQL only)
  const loadRoles = useCallback(async () => {
    if (!connectionId || !selectedDatabase || !isPostgreSQL) return
    
    setIsLoading(true)
    try {
      const response = await AgentProxyService.getRoles(connectionId, selectedDatabase)
      console.log('Roles response:', response) // Debug log
      
      // Check both possible response structures
      let rolesList: Role[] = []
      if (response && typeof response === 'object') {
        if ('roles' in response) {
          rolesList = (response as { roles?: Role[] }).roles ?? []
        } else if ('result' in response && (response as { result?: { roles?: Role[] } }).result?.roles) {
          rolesList = (response as { result?: { roles?: Role[] } }).result?.roles ?? []
        } else if ('data' in response && (response as { data?: { roles?: Role[] } }).data?.roles) {
          rolesList = (response as { data?: { roles?: Role[] } }).data?.roles ?? []
        } else if (Array.isArray(response)) {
          rolesList = response as Role[]
        }
      }
      
      // Map the API response format to our internal format
      const mappedRoles: Role[] = rolesList.map((role) => ({
        role_name: (role as Role & { name?: string }).role_name || (role as { name?: string }).name || '',
        with_login: (role as Role & { canLogin?: boolean }).with_login ?? (role as { canLogin?: boolean }).canLogin ?? true,
        inherit: role.inherit ?? true,
        createDatabase: role.createDatabase ?? false,
        createRole: role.createRole ?? false,
        superuser: role.superuser ?? false,
        replication: role.replication ?? false,
        bypassRls: role.bypassRls ?? false,
        connectionLimit: role.connectionLimit ?? -1,
        validUntil: role.validUntil ?? null,
        created_at: role.created_at
      }))
      
      setRoles(mappedRoles)
      if (mappedRoles.length === 0) {
        console.warn('No roles found in response:', response)
      }
    } catch (error) {
      console.error('Failed to load roles:', error)
      showToast('error', 'Failed to load roles', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [connectionId, selectedDatabase, isPostgreSQL, showToast])
  
  // Load grants for a user
  const loadUserGrants = useCallback(async (username: string, host?: string) => {
    if (!connectionId || !selectedDatabase) return
    
    setIsLoadingUserGrants(true)
    try {
      console.log(`🔍 Loading grants for user: ${username} in database: ${selectedDatabase}`) // Debug log
      const response = await AgentProxyService.getGrants(
        connectionId,
        selectedDatabase,
        username,
        'user',
        isMySQL ? (host || selectedUser?.host || '%') : undefined
      )
      console.log('📋 Raw user grants response:', JSON.stringify(response, null, 2)) // Better debug log
      
      // Check different response structures more thoroughly
      let grantsList: Grant[] = []
      if (response) {
        // Log the structure we received
        console.log('🔍 Response type:', typeof response)
        console.log('🔍 Response keys:', Object.keys(response || {}))
        
        if (typeof response === 'object') {
          // Check for the new structured response format
          if ('grants' in response && response.grants && typeof response.grants === 'object') {
            console.log('📋 Found grants object in response.grants, parsing structured format...')
            const grantsObj = response.grants as {
              global_privileges?: { privileges?: string[]; with_grant_option?: boolean; source?: string }[]
              database_privileges?: { database_name: string; privileges?: string[]; source?: string }[]
              schema_privileges?: { schema_name: string; privileges: Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }> }[]
              table_privileges?: { schema_name?: string; table_name: string; privileges: Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }> }[]
            }
            
            // Transform the structured grants into a flat array format for display
            const transformedGrants: Grant[] = []
            
            // Process global privileges (MySQL)
            if (Array.isArray((grantsObj as Record<string, unknown>).global_privileges)) {
              for (const gp of (grantsObj as { global_privileges: Record<string, unknown>[] }).global_privileges) {
                const privs = (gp as { privileges?: unknown }).privileges
                if (Array.isArray(privs)) {
                  for (const privilege of privs as string[]) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'global',
                      object_name: '*.*',
                      source: (gp as { source?: string }).source,
                      grant_option: !!(gp as { with_grant_option?: boolean }).with_grant_option
                    })
                  }
                }
              }
            }

            // Process database privileges
            if (grantsObj?.database_privileges && Array.isArray(grantsObj.database_privileges)) {
              for (const dbPrivRaw of grantsObj.database_privileges as Record<string, unknown>[]) {
                const databaseName = (dbPrivRaw as { database_name?: string; database?: string }).database_name || (dbPrivRaw as { database?: string }).database || selectedDatabase
                const { privileges } = dbPrivRaw as { privileges?: unknown }
                if (Array.isArray(privileges)) {
                  for (const privilege of privileges as string[]) {
                    transformedGrants.push({
                      privilege: privilege,
                      object_type: 'database',
                      object_name: databaseName,
                      source: (dbPrivRaw as { source?: string }).source,
                      grant_option: false
                    })
                  }
                }
              }
            }
            
            // Process schema privileges
            if (grantsObj?.schema_privileges && Array.isArray(grantsObj.schema_privileges)) {
              for (const schemaPriv of grantsObj.schema_privileges as { schema_name?: string; privileges?: unknown; source?: string }[]) {
                const schemaName = schemaPriv.schema_name || ''
                const raw = schemaPriv.privileges
                // PostgreSQL structured map format: { CREATE: {granted: true}, USAGE: {granted: true} }
                if (raw && !Array.isArray(raw) && typeof raw === 'object') {
                  for (const [privilege, details] of Object.entries(raw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                    const privDetails = details
                    const normalized = String(privilege).toUpperCase()
                    const isValidSchemaPrivilege = normalized === 'CREATE' || normalized === 'USAGE'
                    if (privDetails?.granted && (!isPostgreSQL || isValidSchemaPrivilege)) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'schema',
                        object_name: schemaName,
                        schema_name: schemaName,
                        source: privDetails.source,
                        grant_option: privDetails.grantable || false,
                        grantor: privDetails.grantor
                      })
                    }
                  }
                }
                // MySQL/alternative simple array format: ['USAGE', 'CREATE']
                if (Array.isArray(raw)) {
                  for (const privilege of raw as string[]) {
                    const normalized = String(privilege).toUpperCase()
                    const isValidSchemaPrivilege = normalized === 'CREATE' || normalized === 'USAGE'
                    if (!isPostgreSQL || isValidSchemaPrivilege) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'schema',
                        object_name: schemaName,
                        schema_name: schemaName,
                        source: schemaPriv.source,
                        grant_option: false
                      })
                    }
                  }
                }
              }
            }
            
            // Process table privileges (support both PostgreSQL object map and MySQL string array formats)
            if (grantsObj?.table_privileges && Array.isArray(grantsObj.table_privileges)) {
              for (const tablePriv of grantsObj.table_privileges as Record<string, unknown>[]) {
                const tableNameRaw = (tablePriv as { table_name?: string; table?: string }).table_name || (tablePriv as { table?: string }).table || ''
                const schemaName = (tablePriv as { schema_name?: string; schema?: string }).schema_name || (tablePriv as { schema?: string }).schema
                const databaseNameForTable = (tablePriv as { database_name?: string; database?: string }).database_name || (tablePriv as { database?: string }).database || selectedDatabase
                const { privileges } = tablePriv as { privileges?: unknown }
                const tableName = tableNameRaw
                // PostgreSQL-style: privileges is a map
                if (privileges && !Array.isArray(privileges) && typeof privileges === 'object') {
                  for (const [privilege, details] of Object.entries(privileges as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                    const privDetails = details
                    if (privDetails?.granted) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'table',
                        object_name: tableName,
                        schema_name: schemaName,
                        source: privDetails.source,
                        grant_option: privDetails.grantable || false,
                        grantor: privDetails.grantor
                      })
                    }
                  }
                }
                // MySQL-style: privileges is an array of strings
                if (Array.isArray(privileges)) {
                  for (const privilege of privileges as string[]) {
                    const objectNameMySQL = tableName.includes('.') ? tableName : `${databaseNameForTable}.${tableName}`
                    transformedGrants.push({
                      privilege,
                      object_type: 'table',
                      object_name: isPostgreSQL ? tableName : objectNameMySQL,
                      schema_name: isPostgreSQL ? schemaName : undefined,
                      source: (tablePriv as { source?: string }).source,
                      grant_option: false
                    })
                  }
                }
              }
            }

            // Process sequence privileges (USAGE)
            if (Array.isArray((grantsObj as Record<string, unknown>).sequence_privileges)) {
              for (const seqPriv of (grantsObj as { sequence_privileges: Record<string, unknown>[] }).sequence_privileges) {
                const schemaName = (seqPriv as { schema_name?: string }).schema_name
                const sequenceName = (seqPriv as { sequence_full_name?: string; sequence_name?: string }).sequence_full_name || (seqPriv as { sequence_name?: string }).sequence_name || ''
                const privilegesRaw = (seqPriv as { privileges?: unknown }).privileges
                // Map/object format
                if (privilegesRaw && !Array.isArray(privilegesRaw) && typeof privilegesRaw === 'object') {
                  for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                    const privDetails = details
                    if (privDetails?.granted) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'sequence',
                        object_name: sequenceName,
                        schema_name: schemaName,
                        source: privDetails.source,
                        grant_option: privDetails.grantable || false,
                        grantor: privDetails.grantor
                      })
                    }
                  }
                }
                // Array format
                if (Array.isArray(privilegesRaw)) {
                  for (const privilege of privilegesRaw as string[]) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'sequence',
                      object_name: sequenceName,
                      schema_name: schemaName,
                      source: (seqPriv as { source?: string }).source,
                      grant_option: (seqPriv as { is_grantable?: boolean }).is_grantable || false
                    })
                  }
                }
              }
            }

            // Process function privileges (EXECUTE)
            if (Array.isArray((grantsObj as Record<string, unknown>).function_privileges)) {
              for (const funcPriv of (grantsObj as { function_privileges: Record<string, unknown>[] }).function_privileges) {
                const schemaName = (funcPriv as { schema_name?: string }).schema_name
                const functionName = (funcPriv as { function_signature?: string; function_name?: string }).function_signature || (funcPriv as { function_name?: string }).function_name || ''
                const privilegesRaw = (funcPriv as { privileges?: unknown }).privileges
                if (privilegesRaw && !Array.isArray(privilegesRaw) && typeof privilegesRaw === 'object') {
                  for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                    const privDetails = details
                    if (privDetails?.granted) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'function',
                        object_name: functionName,
                        schema_name: schemaName,
                        source: privDetails.source,
                        grant_option: privDetails.grantable || false,
                        grantor: privDetails.grantor
                      })
                    }
                  }
                }
                if (Array.isArray(privilegesRaw)) {
                  for (const privilege of privilegesRaw as string[]) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'function',
                      object_name: functionName,
                      schema_name: schemaName,
                      source: (funcPriv as { source?: string }).source,
                      grant_option: (funcPriv as { is_grantable?: boolean }).is_grantable || false
                    })
                  }
                }
              }
            }

            // Process column privileges
            if (Array.isArray((grantsObj as Record<string, unknown>).column_privileges)) {
              for (const colPriv of (grantsObj as { column_privileges: Record<string, unknown>[] }).column_privileges) {
                const schemaName = (colPriv as { schema_name?: string }).schema_name
                const tableName = (colPriv as { table_name?: string }).table_name || ''
                const columnName = (colPriv as { column_name?: string }).column_name || ''
                const objectName = columnName ? `${tableName}.${columnName}` : tableName
                const privilegesRaw = (colPriv as { privileges?: unknown }).privileges
                if (Array.isArray(privilegesRaw)) {
                  for (const privilege of privilegesRaw as string[]) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'column',
                      object_name: objectName,
                      schema_name: schemaName,
                      source: (colPriv as { source?: string }).source,
                      grant_option: false
                    })
                  }
                } else if (privilegesRaw && typeof privilegesRaw === 'object') {
                  for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                    const privDetails = details
                    if (privDetails?.granted) {
                      transformedGrants.push({
                        privilege,
                        object_type: 'column',
                        object_name: objectName,
                        schema_name: schemaName,
                        source: privDetails.source,
                        grant_option: privDetails.grantable || false,
                        grantor: privDetails.grantor
                      })
                    }
                  }
                }
              }
            }

            // Effective permissions (e.g., can_read_objects: ["db.table"]) → infer SELECT table grants only when clearly table-qualified
            const canReadObjects = (response as { grants?: { effective_permissions?: { can_read_objects?: string[] } } }).grants?.effective_permissions?.can_read_objects
            if (Array.isArray(canReadObjects)) {
              for (const obj of canReadObjects) {
                if (typeof obj !== 'string') continue
                const objectName = obj.trim()
                // Skip labeled entries like "database:xxx" or anything without a dot separator
                if (objectName.includes(':')) continue
                if (!objectName.includes('.')) continue
                transformedGrants.push({
                  privilege: 'SELECT',
                  object_type: 'table',
                  object_name: objectName,
                  grant_option: false
                })
              }
            }
            
            grantsList = transformedGrants
            console.log(`📋 Transformed structured grants into ${transformedGrants.length} grant entries:`, grantsList)
            
          } else if (Array.isArray((response as { grants?: Grant[] }).grants)) {
            // Legacy format where grants is an array
            grantsList = (response as { grants?: Grant[] }).grants ?? []
            console.log('📋 Found grants array in response.grants:', grantsList)
          } else if ('result' in response && (response as { result?: { grants?: Grant[] } }).result?.grants) {
            grantsList = Array.isArray((response as { result?: { grants?: Grant[] } }).result!.grants)
              ? (response as { result?: { grants?: Grant[] } }).result!.grants!
              : []
            console.log('📋 Found grants in response.result.grants:', grantsList)
          } else if ('data' in response && (response as { data?: { grants?: Grant[] } }).data?.grants) {
            grantsList = Array.isArray((response as { data?: { grants?: Grant[] } }).data!.grants)
              ? (response as { data?: { grants?: Grant[] } }).data!.grants!
              : []
            console.log('📋 Found grants in response.data.grants:', grantsList)
          } else if (Array.isArray(response)) {
            grantsList = response as Grant[]
            console.log('📋 Response is array directly:', grantsList)
          } else {
            console.log('🔍 No recognizable grants structure found')
          }
        }
      }
      
      // Ensure it's always an array and de-duplicate identical entries
      if (!Array.isArray(grantsList)) {
        console.log('⚠️ grantsList is not array, setting to empty:', grantsList)
        grantsList = []
      }
      const unique = new Map<string, Grant>()
      for (const g of grantsList) {
        const key = `${g.privilege}|${g.object_type}|${g.object_name}|${g.schema_name || ''}`
        if (!unique.has(key)) unique.set(key, g)
      }
      grantsList = Array.from(unique.values())
      
      console.log(`✅ Final grants for user ${username}: ${grantsList.length} grants`, grantsList)
      setUserGrants(grantsList)
    } catch (error) {
      console.error('❌ Failed to load user grants:', error)
      setUserGrants([]) // Set empty array on error
    } finally {
      setIsLoadingUserGrants(false)
    }
  }, [connectionId, selectedDatabase, isMySQL, isPostgreSQL, selectedUser])
  
  // Load grants for a role
  const loadRoleGrants = useCallback(async (roleName: string) => {
    if (!connectionId || !selectedDatabase || !isPostgreSQL) return
    
    setIsLoadingRoleGrants(true)
    try {
      console.log(`🔍 Loading grants for role: ${roleName} in database: ${selectedDatabase}`) // Debug log
      const response = await AgentProxyService.getGrants(connectionId, selectedDatabase, roleName, 'role')
      console.log('📋 Raw role grants response:', JSON.stringify(response, null, 2)) // Better debug log
      
      // Check different response structures using same logic as user grants
      let grantsList: Grant[] = []
      if (response && typeof response === 'object') {
        // Check for the new structured response format
        if ('grants' in response && response.grants && typeof response.grants === 'object') {
          console.log('📋 Found grants object in response.grants, parsing structured format...')
          const grantsObj = response.grants as {
            database_privileges?: { database_name: string; privileges?: string[]; source?: string }[]
            schema_privileges?: { schema_name: string; privileges: Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }> }[]
            table_privileges?: { schema_name?: string; table_name: string; privileges: Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }> }[]
          }
          
          // Transform the structured grants into a flat array format for display
          const transformedGrants: Grant[] = []
          
          // Process database privileges
          if (grantsObj?.database_privileges && Array.isArray(grantsObj.database_privileges)) {
            for (const dbPrivRaw of grantsObj.database_privileges as Record<string, unknown>[]) {
              const databaseName = (dbPrivRaw as { database_name?: string; database?: string }).database_name || (dbPrivRaw as { database?: string }).database || selectedDatabase
              const { privileges } = dbPrivRaw as { privileges?: unknown }
              if (Array.isArray(privileges)) {
                for (const privilege of privileges as string[]) {
                  transformedGrants.push({
                    privilege: privilege,
                    object_type: 'database',
                    object_name: databaseName,
                    source: (dbPrivRaw as { source?: string }).source,
                    grant_option: false
                  })
                }
              }
            }
          }
          
          // Process schema privileges
          if (grantsObj?.schema_privileges && Array.isArray(grantsObj.schema_privileges)) {
            for (const schemaPriv of grantsObj.schema_privileges as { schema_name?: string; privileges?: unknown; source?: string }[]) {
              const schemaName = schemaPriv.schema_name || ''
              const raw = schemaPriv.privileges
              // PostgreSQL structured map format
              if (raw && !Array.isArray(raw) && typeof raw === 'object') {
                for (const [privilege, details] of Object.entries(raw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                  const privDetails = details
                  const normalized = String(privilege).toUpperCase()
                  const isValidSchemaPrivilege = normalized === 'CREATE' || normalized === 'USAGE'
                  if (privDetails?.granted && (!isPostgreSQL || isValidSchemaPrivilege)) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'schema',
                      object_name: schemaName,
                      schema_name: schemaName,
                      source: privDetails.source,
                      grant_option: privDetails.grantable || false,
                      grantor: privDetails.grantor
                    })
                  }
                }
              }
              // Simple array format
              if (Array.isArray(raw)) {
                for (const privilege of raw as string[]) {
                  const normalized = String(privilege).toUpperCase()
                  const isValidSchemaPrivilege = normalized === 'CREATE' || normalized === 'USAGE'
                  if (!isPostgreSQL || isValidSchemaPrivilege) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'schema',
                      object_name: schemaName,
                      schema_name: schemaName,
                      source: schemaPriv.source,
                      grant_option: false
                    })
                  }
                }
              }
            }
          }
          
          // Process table privileges (support both PostgreSQL map and MySQL array formats)
          if (grantsObj?.table_privileges && Array.isArray(grantsObj.table_privileges)) {
            for (const tablePriv of grantsObj.table_privileges as Record<string, unknown>[]) {
              const tableNameRaw = (tablePriv as { table_name?: string; table?: string }).table_name || (tablePriv as { table?: string }).table || ''
              const schemaName = (tablePriv as { schema_name?: string; schema?: string }).schema_name || (tablePriv as { schema?: string }).schema
              const databaseNameForTable = (tablePriv as { database_name?: string; database?: string }).database_name || (tablePriv as { database?: string }).database || selectedDatabase
              const { privileges } = tablePriv as { privileges?: unknown }
              const tableName = tableNameRaw
              // PostgreSQL: object map
              if (privileges && !Array.isArray(privileges) && typeof privileges === 'object') {
                for (const [privilege, details] of Object.entries(privileges as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                  const privDetails = details
                  if (privDetails?.granted) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'table',
                      object_name: tableName,
                      schema_name: schemaName,
                      source: privDetails.source,
                      grant_option: privDetails.grantable || false,
                      grantor: privDetails.grantor
                    })
                  }
                }
              }
              // MySQL: array of strings
              if (Array.isArray(privileges)) {
                for (const privilege of privileges as string[]) {
                  const objectNameMySQL = tableName.includes('.') ? tableName : `${databaseNameForTable}.${tableName}`
                  transformedGrants.push({
                    privilege,
                    object_type: 'table',
                    object_name: isPostgreSQL ? tableName : objectNameMySQL,
                    schema_name: isPostgreSQL ? schemaName : undefined,
                    source: (tablePriv as { source?: string }).source,
                    grant_option: false
                  })
                }
              }
            }
          }

          // Process sequence privileges
          if (Array.isArray((grantsObj as Record<string, unknown>).sequence_privileges)) {
            for (const seqPriv of (grantsObj as { sequence_privileges: Record<string, unknown>[] }).sequence_privileges) {
              const schemaName = (seqPriv as { schema_name?: string }).schema_name
              const sequenceName = (seqPriv as { sequence_full_name?: string; sequence_name?: string }).sequence_full_name || (seqPriv as { sequence_name?: string }).sequence_name || ''
              const privilegesRaw = (seqPriv as { privileges?: unknown }).privileges
              if (privilegesRaw && !Array.isArray(privilegesRaw) && typeof privilegesRaw === 'object') {
                for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                  const privDetails = details
                  if (privDetails?.granted) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'sequence',
                      object_name: sequenceName,
                      schema_name: schemaName,
                      source: privDetails.source,
                      grant_option: privDetails.grantable || false,
                      grantor: privDetails.grantor
                    })
                  }
                }
              }
              if (Array.isArray(privilegesRaw)) {
                for (const privilege of privilegesRaw as string[]) {
                  transformedGrants.push({
                    privilege,
                    object_type: 'sequence',
                    object_name: sequenceName,
                    schema_name: schemaName,
                    source: (seqPriv as { source?: string }).source,
                    grant_option: (seqPriv as { is_grantable?: boolean }).is_grantable || false
                  })
                }
              }
            }
          }

          // Process function privileges
          if (Array.isArray((grantsObj as Record<string, unknown>).function_privileges)) {
            for (const funcPriv of (grantsObj as { function_privileges: Record<string, unknown>[] }).function_privileges) {
              const schemaName = (funcPriv as { schema_name?: string }).schema_name
              const functionName = (funcPriv as { function_signature?: string; function_name?: string }).function_signature || (funcPriv as { function_name?: string }).function_name || ''
              const privilegesRaw = (funcPriv as { privileges?: unknown }).privileges
              if (privilegesRaw && !Array.isArray(privilegesRaw) && typeof privilegesRaw === 'object') {
                for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                  const privDetails = details
                  if (privDetails?.granted) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'function',
                      object_name: functionName,
                      schema_name: schemaName,
                      source: privDetails.source,
                      grant_option: privDetails.grantable || false,
                      grantor: privDetails.grantor
                    })
                  }
                }
              }
              if (Array.isArray(privilegesRaw)) {
                for (const privilege of privilegesRaw as string[]) {
                  transformedGrants.push({
                    privilege,
                    object_type: 'function',
                    object_name: functionName,
                    schema_name: schemaName,
                    source: (funcPriv as { source?: string }).source,
                    grant_option: (funcPriv as { is_grantable?: boolean }).is_grantable || false
                  })
                }
              }
            }
          }

          // Process column privileges
          if (Array.isArray((grantsObj as Record<string, unknown>).column_privileges)) {
            for (const colPriv of (grantsObj as { column_privileges: Record<string, unknown>[] }).column_privileges) {
              const schemaName = (colPriv as { schema_name?: string }).schema_name
              const tableName = (colPriv as { table_name?: string }).table_name || ''
              const columnName = (colPriv as { column_name?: string }).column_name || ''
              const objectName = columnName ? `${tableName}.${columnName}` : tableName
              const privilegesRaw = (colPriv as { privileges?: unknown }).privileges
              if (Array.isArray(privilegesRaw)) {
                for (const privilege of privilegesRaw as string[]) {
                  transformedGrants.push({
                    privilege,
                    object_type: 'column',
                    object_name: objectName,
                    schema_name: schemaName,
                    source: (colPriv as { source?: string }).source,
                    grant_option: false
                  })
                }
              } else if (privilegesRaw && typeof privilegesRaw === 'object') {
                for (const [privilege, details] of Object.entries(privilegesRaw as Record<string, { granted?: boolean; grantable?: boolean; source?: string; grantor?: string }>)) {
                  const privDetails = details
                  if (privDetails?.granted) {
                    transformedGrants.push({
                      privilege,
                      object_type: 'column',
                      object_name: objectName,
                      schema_name: schemaName,
                      source: privDetails.source,
                      grant_option: privDetails.grantable || false,
                      grantor: privDetails.grantor
                    })
                  }
                }
              }
            }
          }

          // Effective permissions → infer SELECT table grants only for db.table strings (skip labels like "database:")
          const canReadObjects = (response as { grants?: { effective_permissions?: { can_read_objects?: string[] } } }).grants?.effective_permissions?.can_read_objects
          if (Array.isArray(canReadObjects)) {
            for (const obj of canReadObjects) {
              if (typeof obj !== 'string') continue
              const objectName = obj.trim()
              if (objectName.includes(':')) continue
              if (!objectName.includes('.')) continue
              transformedGrants.push({
                privilege: 'SELECT',
                object_type: 'table',
                object_name: objectName,
                grant_option: false
              })
            }
          }
          
          grantsList = transformedGrants
          console.log(`📋 Transformed structured grants into ${transformedGrants.length} grant entries:`, grantsList)
          
        } else if (Array.isArray((response as { grants?: Grant[] }).grants)) {
          // Legacy format where grants is an array
          grantsList = (response as { grants?: Grant[] }).grants ?? []
          console.log('📋 Found grants array in response.grants:', grantsList)
        } else if ('result' in response && (response as { result?: { grants?: Grant[] } }).result?.grants) {
          grantsList = Array.isArray((response as { result?: { grants?: Grant[] } }).result!.grants)
            ? (response as { result?: { grants?: Grant[] } }).result!.grants!
            : []
          console.log('📋 Found grants in response.result.grants:', grantsList)
        } else if ('data' in response && (response as { data?: { grants?: Grant[] } }).data?.grants) {
          grantsList = Array.isArray((response as { data?: { grants?: Grant[] } }).data!.grants)
            ? (response as { data?: { grants?: Grant[] } }).data!.grants!
            : []
          console.log('📋 Found grants in response.data.grants:', grantsList)
        } else if (Array.isArray(response)) {
          grantsList = response as Grant[]
          console.log('📋 Response is array directly:', grantsList)
        }
      }
      
      // Ensure it's always an array and de-duplicate identical entries
      if (!Array.isArray(grantsList)) {
        console.log('⚠️ grantsList is not array, setting to empty:', grantsList)
        grantsList = []
      }
      const unique = new Map<string, Grant>()
      for (const g of grantsList) {
        const key = `${g.privilege}|${g.object_type}|${g.object_name}|${g.schema_name || ''}`
        if (!unique.has(key)) unique.set(key, g)
      }
      grantsList = Array.from(unique.values())
      
      console.log(`✅ Final grants for role ${roleName}: ${grantsList.length} grants`, grantsList)
      setRoleGrants(grantsList)
    } catch (error) {
      console.error('❌ Failed to load role grants:', error)
      setRoleGrants([]) // Set empty array on error
    } finally {
      setIsLoadingRoleGrants(false)
    }
  }, [connectionId, selectedDatabase, isPostgreSQL])
  
  // Load data when database changes
  useEffect(() => {
    if (selectedDatabase) {
      if (activeTab === 'users') {
        void loadUsers()
      } else if (activeTab === 'roles' && isPostgreSQL) {
        void loadRoles()
      } else if (activeTab === 'privileges') {
        // Privileges tab needs both users and roles
        void loadUsers()
        if (isPostgreSQL) {
          void loadRoles()
        }
      }
    }
  }, [selectedDatabase, activeTab, loadUsers, loadRoles, isPostgreSQL])
  
  // Load grants when user/role selection changes
  useEffect(() => {
    if (selectedUser?.user_name) {
      void loadUserGrants(selectedUser.user_name, isMySQL ? (selectedUser.host || '%') : undefined)
    }
  }, [selectedUser, loadUserGrants, isMySQL])
  
  useEffect(() => {
    if (selectedRole?.role_name) {
      void loadRoleGrants(selectedRole.role_name)
    }
  }, [selectedRole, loadRoleGrants])
  
  // Load grants when switching to privileges tab
  useEffect(() => {
    if (activeTab === 'privileges') {
      if (selectedUser?.user_name) {
        void loadUserGrants(selectedUser.user_name, isMySQL ? (selectedUser.host || '%') : undefined)
      }
      if (selectedRole?.role_name) {
        void loadRoleGrants(selectedRole.role_name)
      }
    }
  }, [activeTab, selectedUser?.user_name, selectedUser?.host, selectedRole?.role_name, loadUserGrants, loadRoleGrants, isMySQL])

  // Keep selected user in sync with latest loaded users (so edit dialogs show fresh values)
  useEffect(() => {
    if (!selectedUser) return
    const updated = users.find(u =>
      u.user_name === selectedUser.user_name && (isMySQL ? (u.host || 'localhost') === (selectedUser.host || 'localhost') : true)
    )
    if (updated && updated !== selectedUser) {
      setSelectedUser(updated)
    }
  }, [users, selectedUser, isMySQL])

  // Load user dependencies for modals when user is selected (PostgreSQL only)
  const loadCurrentUserDeps = useCallback(async () => {
    if (!selectedUser?.user_name || !connectionId || !selectedDatabase || !isPostgreSQL) {
      setCurrentUserDeps(null)
      return
    }

    try {
      const userDetails = await AgentProxyService.getUserDetails(connectionId, selectedDatabase, selectedUser.user_name, true)
        
        let memberOfRoles: string[] = []
        let grantedRoles: string[] = []
        let owned = { 
          schemas: 0, relations: 0, databases: 0, 
          functions: 0, types: 0, languages: 0, extensions: 0, collations: 0, 
          conversions: 0, foreign_servers: 0, foreign_data_wrappers: 0 
        }
        let ownedDetails = {
          databases: [] as string[], schemas: [] as string[], relations: [] as string[],
          functions: [] as string[], types: [] as string[], languages: [] as string[],
          extensions: [] as string[], collations: [] as string[], conversions: [] as string[],
          foreign_servers: [] as string[], foreign_data_wrappers: [] as string[]
        }
        let memberships = { is_member_of: [] as string[], has_members: [] as string[] }
        let grants = {
          summary: null as {
            username: string
            database_type: string
            total_privileges: number
            privilege_categories: {
              read_privileges: number
              write_privileges: number
              admin_privileges: number
              inherited_privileges: number
            }
          } | null,
          role_memberships: [] as {
            role_name: string
            admin_option: boolean
            role_privileges: {
              superuser: boolean
              create_database: boolean
              create_role: boolean
            }
            source: string
          }[],
          database_privileges: [] as {
            database_name: string
            privileges: string[]
            object_type: string
            source: string
          }[],
          schema_privileges: [] as {
            database_name: string
            schema_name: string
            privileges: string[]
            source: string
          }[],
          table_privileges: [] as {
            database_name: string
            schema_name: string
            table_name: string
            privileges: string[]
            source: string
          }[],
          column_privileges: [] as {
            database_name: string
            schema_name: string
            table_name: string
            column_name: string
            privileges: string[]
            source: string
          }[],
          function_privileges: [] as {
            schema_name: string
            function_name: string
            function_signature: string
            privilege_type: string
            privileges: string[]
            is_grantable: boolean
            grantor: string
            object_type: string
            source: string
          }[],
          sequence_privileges: [] as {
            schema_name: string
            sequence_name: string
            sequence_full_name: string
            privilege_type: string
            privileges: string[]
            is_grantable: boolean
            grantor: string
            object_type: string
            source: string
          }[],
          effective_permissions: {
            can_read_objects: [] as string[],
            can_write_objects: [] as string[],
            can_create_in: [] as string[],
            can_delete_from: [] as string[],
            admin_objects: [] as string[]
          }
        }
        let defaultPrivilegesCount = 0

        // Extract dependency information
        const userDetailsObj = userDetails as Record<string, unknown>
        
        // Check for user_details wrapper (response might be wrapped)
        const actualUserDetails = userDetailsObj.user_details ? userDetailsObj.user_details as Record<string, unknown> : userDetailsObj
        
        if ('dependencies' in actualUserDetails) {
          const deps = actualUserDetails.dependencies as Record<string, unknown>
          
          // Extract memberships
          if (deps.memberships && typeof deps.memberships === 'object') {
            const membershipData = deps.memberships as { is_member_of?: unknown[]; has_members?: unknown[] }
            memberOfRoles = Array.isArray(membershipData.is_member_of) ? membershipData.is_member_of as string[] : []
            grantedRoles = Array.isArray(membershipData.has_members) ? membershipData.has_members as string[] : []
            memberships = {
              is_member_of: memberOfRoles,
              has_members: grantedRoles
            }
          }
          
          // Extract owned object details
          if (deps.owned_objects && typeof deps.owned_objects === 'object') {
            const ownedObjects = deps.owned_objects as Record<string, unknown[]>
            
            // Extract detailed lists
            ownedDetails = {
              databases: Array.isArray(ownedObjects.databases) ? ownedObjects.databases as string[] : [],
              schemas: Array.isArray(ownedObjects.schemas) ? ownedObjects.schemas as string[] : [],
              relations: Array.isArray(ownedObjects.relations) ? ownedObjects.relations as string[] : [],
              functions: Array.isArray(ownedObjects.functions) ? ownedObjects.functions as string[] : [],
              types: Array.isArray(ownedObjects.types) ? ownedObjects.types as string[] : [],
              languages: Array.isArray(ownedObjects.languages) ? ownedObjects.languages as string[] : [],
              extensions: Array.isArray(ownedObjects.extensions) ? ownedObjects.extensions as string[] : [],
              collations: Array.isArray(ownedObjects.collations) ? ownedObjects.collations as string[] : [],
              conversions: Array.isArray(ownedObjects.conversions) ? ownedObjects.conversions as string[] : [],
              foreign_servers: Array.isArray(ownedObjects.foreign_servers) ? ownedObjects.foreign_servers as string[] : [],
              foreign_data_wrappers: Array.isArray(ownedObjects.foreign_data_wrappers) ? ownedObjects.foreign_data_wrappers as string[] : []
            }
            
            // Calculate counts
            // Note: relations includes tables, views, materialized views, and sequences
            owned = {
              databases: ownedDetails.databases.length,
              schemas: ownedDetails.schemas.length,
              relations: ownedDetails.relations.length, // All tables, views, materialized views, sequences
              functions: ownedDetails.functions.length,
              types: ownedDetails.types.length,
              languages: ownedDetails.languages.length,
              extensions: ownedDetails.extensions.length,
              collations: ownedDetails.collations.length,
              conversions: ownedDetails.conversions.length,
              foreign_servers: ownedDetails.foreign_servers.length,
              foreign_data_wrappers: ownedDetails.foreign_data_wrappers.length
            }
          }

          // Extract owned object counts (fallback if owned_objects not available)
          if (deps.owned_counts && typeof deps.owned_counts === 'object') {
            const ownedCounts = deps.owned_counts as Record<string, number>
            owned = {
              schemas: ownedCounts.schemas || owned.schemas,
              // Combine tables, views, sequences into relations
              relations: (ownedCounts.tables || 0) + (ownedCounts.views || 0) + (ownedCounts.sequences || 0) + (ownedCounts.relations || owned.relations),
              databases: ownedCounts.databases || owned.databases,
              functions: ownedCounts.functions || owned.functions,
              types: ownedCounts.types || owned.types,
              languages: ownedCounts.languages || owned.languages,
              extensions: ownedCounts.extensions || owned.extensions,
              collations: ownedCounts.collations || owned.collations,
              conversions: ownedCounts.conversions || owned.conversions,
              foreign_servers: ownedCounts.foreign_servers || owned.foreign_servers,
              foreign_data_wrappers: ownedCounts.foreign_data_wrappers || owned.foreign_data_wrappers
            }
          }

          // Extract grants information
          if (deps.grants && typeof deps.grants === 'object') {
            const grantsData = deps.grants as Record<string, unknown>
            
            // Extract grants summary
            if (grantsData.summary && typeof grantsData.summary === 'object') {
              const summary = grantsData.summary as Record<string, unknown>
              grants.summary = {
                username: (summary.username as string) || '',
                database_type: (summary.database_type as string) || '',
                total_privileges: (summary.total_privileges as number) || 0,
                privilege_categories: {
                  read_privileges: ((summary.privilege_categories as Record<string, unknown>)?.read_privileges as number) || 0,
                  write_privileges: ((summary.privilege_categories as Record<string, unknown>)?.write_privileges as number) || 0,
                  admin_privileges: ((summary.privilege_categories as Record<string, unknown>)?.admin_privileges as number) || 0,
                  inherited_privileges: ((summary.privilege_categories as Record<string, unknown>)?.inherited_privileges as number) || 0
                }
              }
            }
            
            // Extract role memberships
            if (Array.isArray(grantsData.role_memberships)) {
              grants.role_memberships = grantsData.role_memberships.map((membership: unknown) => {
                const m = membership as Record<string, unknown>
                return {
                  role_name: (m.role_name as string) || '',
                  admin_option: (m.admin_option as boolean) || false,
                  role_privileges: {
                    superuser: ((m.role_privileges as Record<string, unknown>)?.superuser as boolean) || false,
                    create_database: ((m.role_privileges as Record<string, unknown>)?.create_database as boolean) || false,
                    create_role: ((m.role_privileges as Record<string, unknown>)?.create_role as boolean) || false
                  },
                  source: (m.source as string) || ''
                }
              })
            }
            
            // Extract database privileges
            if (Array.isArray(grantsData.database_privileges)) {
              grants.database_privileges = grantsData.database_privileges.map((dbPriv: unknown) => {
                const dp = dbPriv as Record<string, unknown>
                return {
                  database_name: (dp.database_name as string) || '',
                  privileges: Array.isArray(dp.privileges) ? (dp.privileges as string[]) : [],
                  object_type: (dp.object_type as string) || '',
                  source: (dp.source as string) || ''
                }
              })
            }
            
            // Extract schema privileges
            if (Array.isArray(grantsData.schema_privileges)) {
              grants.schema_privileges = grantsData.schema_privileges.map((schemaPriv: unknown) => {
                const sp = schemaPriv as Record<string, unknown>
                // Handle privileges - might be array of strings or objects
                let privileges: string[] = []
                if (Array.isArray(sp.privileges)) {
                  privileges = sp.privileges.map((p: unknown) => {
                    if (typeof p === 'string') return p
                    if (typeof p === 'object' && p && 'privilege' in (p as Record<string, unknown>)) {
                      return ((p as Record<string, unknown>).privilege as string) || ''
                    }
                    return String(p)
                  }).filter(Boolean)
                }
                // If no privileges but has privilege_type, use that
                if (privileges.length === 0 && sp.privilege_type) {
                  privileges = [sp.privilege_type as string]
                }
                // Common schema privileges if empty for any schema with 'direct' source
                if (privileges.length === 0 && sp.source === 'direct') {
                  // Direct source typically means USAGE for schemas
                  privileges = ['USAGE']
                }

                return {
                  database_name: (sp.database_name as string) || '',
                  schema_name: (sp.schema_name as string) || '',
                  privileges,
                  source: (sp.source as string) || ''
                }
              })
            }
            
            // Extract table privileges
            if (Array.isArray(grantsData.table_privileges)) {
              grants.table_privileges = grantsData.table_privileges.map((tablePriv: unknown) => {
                const tp = tablePriv as Record<string, unknown>
                return {
                  database_name: (tp.database_name as string) || '',
                  schema_name: (tp.schema_name as string) || '',
                  table_name: (tp.table_name as string) || '',
                  privileges: Array.isArray(tp.privileges) ? (tp.privileges as string[]) : [],
                  source: (tp.source as string) || ''
                }
              })
            }
            
            // Extract column privileges
            if (Array.isArray(grantsData.column_privileges)) {
              grants.column_privileges = grantsData.column_privileges.map((colPriv: unknown) => {
                const cp = colPriv as Record<string, unknown>
                return {
                  database_name: (cp.database_name as string) || '',
                  schema_name: (cp.schema_name as string) || '',
                  table_name: (cp.table_name as string) || '',
                  column_name: (cp.column_name as string) || '',
                  privileges: Array.isArray(cp.privileges) ? (cp.privileges as string[]) : [],
                  source: (cp.source as string) || ''
                }
              })
            }
            
            // Extract function privileges
            if (Array.isArray(grantsData.function_privileges)) {
              grants.function_privileges = grantsData.function_privileges.map((funcPriv: unknown) => {
                const fp = funcPriv as Record<string, unknown>
                // Handle privileges - use privilege_type if privileges array is empty
                let privileges: string[] = []
                if (Array.isArray(fp.privileges) && fp.privileges.length > 0) {
                  privileges = fp.privileges.map((p: unknown) => {
                    if (typeof p === 'string') return p
                    return String(p)
                  }).filter(Boolean)
                }
                // If no privileges array but has privilege_type, use that
                if (privileges.length === 0 && fp.privilege_type) {
                  privileges = [fp.privilege_type as string]
                }
                return {
                  schema_name: (fp.schema_name as string) || '',
                  function_name: (fp.function_name as string) || '',
                  function_signature: (fp.function_signature as string) || '',
                  privilege_type: (fp.privilege_type as string) || '',
                  privileges,
                  is_grantable: (fp.is_grantable as boolean) || false,
                  grantor: (fp.grantor as string) || '',
                  object_type: (fp.object_type as string) || '',
                  source: (fp.source as string) || ''
                }
              })
            }
            
            // Extract sequence privileges
            if (Array.isArray(grantsData.sequence_privileges)) {
              grants.sequence_privileges = grantsData.sequence_privileges.map((seqPriv: unknown) => {
                const sp = seqPriv as Record<string, unknown>
                // Handle privileges - use privilege_type if privileges array is empty
                let privileges: string[] = []
                if (Array.isArray(sp.privileges) && sp.privileges.length > 0) {
                  privileges = sp.privileges.map((p: unknown) => {
                    if (typeof p === 'string') return p
                    return String(p)
                  }).filter(Boolean)
                }
                // If no privileges array but has privilege_type, use that
                if (privileges.length === 0 && sp.privilege_type) {
                  privileges = [sp.privilege_type as string]
                }

                return {
                  schema_name: (sp.schema_name as string) || '',
                  sequence_name: (sp.sequence_name as string) || '',
                  sequence_full_name: (sp.sequence_full_name as string) || '',
                  privilege_type: (sp.privilege_type as string) || '',
                  privileges,
                  is_grantable: (sp.is_grantable as boolean) || false,
                  grantor: (sp.grantor as string) || '',
                  object_type: (sp.object_type as string) || '',
                  source: (sp.source as string) || ''
                }
              })
            }
            
            // Extract effective permissions
            if (grantsData.effective_permissions && typeof grantsData.effective_permissions === 'object') {
              const effectivePerms = grantsData.effective_permissions as Record<string, unknown>
              grants.effective_permissions = {
                can_read_objects: Array.isArray(effectivePerms.can_read_objects) ? (effectivePerms.can_read_objects as string[]) : [],
                can_write_objects: Array.isArray(effectivePerms.can_write_objects) ? (effectivePerms.can_write_objects as string[]) : [],
                can_create_in: Array.isArray(effectivePerms.can_create_in) ? (effectivePerms.can_create_in as string[]) : [],
                can_delete_from: Array.isArray(effectivePerms.can_delete_from) ? (effectivePerms.can_delete_from as string[]) : [],
                admin_objects: Array.isArray(effectivePerms.admin_objects) ? (effectivePerms.admin_objects as string[]) : []
              }
            }
          }

          // Extract default privileges count
          if (deps.default_privileges_count && typeof deps.default_privileges_count === 'number') {
            defaultPrivilegesCount = deps.default_privileges_count
          }
        }

        setCurrentUserDeps({
          memberOfRoles,
          grantedRoles,
          defaultPrivilegesCount,
          owned,
          ownedDetails,
          memberships,
          grants
        })
    } catch (error) {
      console.error('Failed to load user dependencies:', error)
      setCurrentUserDeps(null)
    }
  }, [selectedUser?.user_name, connectionId, selectedDatabase, isPostgreSQL])

  // Call loadCurrentUserDeps when dependencies change
  useEffect(() => {
    void loadCurrentUserDeps()
  }, [loadCurrentUserDeps])

  // Keep selected role in sync with latest loaded roles
  useEffect(() => {
    if (!selectedRole) return
    const updated = roles.find(r => r.role_name === selectedRole.role_name)
    if (updated && updated !== selectedRole) {
      setSelectedRole(updated)
    }
  }, [roles, selectedRole])
  
  // Filter users based on search
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchLower = searchTerm.toLowerCase()
      return (
        user.user_name?.toLowerCase().includes(searchLower) ||
        user.host?.toLowerCase().includes(searchLower)
      )
    })
  }, [users, searchTerm])
  
  // Filter roles based on search
  const filteredRoles = useMemo(() => {
    return roles.filter(role => 
      role.role_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [roles, searchTerm])
  
  // Delete user
  const handleDeleteUser = async (user: DatabaseUser) => {
    if (!connectionId || !selectedDatabase || !user.user_name) return
    
    try {
      await AgentProxyService.dropUser(connectionId, selectedDatabase, user.user_name)
      showToast('success', `User "${user.user_name}" deleted successfully`)
      await loadUsers()
      if (selectedUser?.user_name === user.user_name) {
        setSelectedUser(null)
        setUserGrants([])
      }
    } catch (error) {
      // Try to parse dependency diagnostics returned by backend
      let description = error instanceof Error ? error.message : 'Unknown error'
      let dependencyDetails: unknown = undefined
      if (error && typeof error === 'object') {
        const errObj = error as Record<string, unknown>
        if (typeof errObj.dependencies === 'string') {
          // May be a JSON string from server: attempt to parse
          try { dependencyDetails = typeof errObj.dependencies === 'string' ? JSON.parse(errObj.dependencies) : errObj.dependencies } catch { dependencyDetails = errObj.dependencies }
        } else if (errObj.dependencies) {
          dependencyDetails = errObj.dependencies
        }
        // remediation field can be present; not used directly but leave parsed in future as needed
      }

      // Build a concise human hint if dependencies are present
      if (dependencyDetails && typeof dependencyDetails === 'object') {
        try {
          const dep = dependencyDetails as { owned_objects?: Record<string, unknown>; memberships?: { is_member_of?: unknown[]; has_members?: unknown[] } }
          const owned = (typeof dep.owned_objects === 'object' && dep.owned_objects !== null) ? dep.owned_objects : undefined
          const counts = owned ? Object.entries(owned).map(([, v]) => Array.isArray(v) ? v.length : 0).reduce((a, b) => a + b, 0) : 0
          const memberships = dep.memberships?.is_member_of ?? []
          const summary = `Owned objects: ${counts}${Array.isArray(memberships) && memberships.length > 0 ? `, member of ${memberships.length} role(s)` : ''}`
          description = `${description}\n${summary}`
          // Persist the raw diagnostic for the selected user to enrich the Dependency Insight panel
          if (user.user_name) {
            setDeletionDepsByUser(prev => ({ ...prev, [user.user_name!]: dependencyDetails }))
          }
        } catch { /* ignore summarization errors */ }
      }

      showToast('error', 'Failed to delete user', description)
    }
  }
  
  // Delete role
  const handleDeleteRole = async (role: Role) => {
    if (!connectionId || !selectedDatabase || !role.role_name) return
    
    try {
      await AgentProxyService.dropRole(connectionId, selectedDatabase, role.role_name)
      showToast('success', `Role "${role.role_name}" deleted successfully`)
      await loadRoles()
      if (selectedRole?.role_name === role.role_name) {
        setSelectedRole(null)
        setRoleGrants([])
      }
    } catch (error) {
      showToast('error', 'Failed to delete role', error instanceof Error ? error.message : 'Unknown error')
    }
  }
  
  const confirmDelete = async () => {
    if (!deleteConfirmItem) return
    
    if (deleteConfirmItem.type === 'user') {
      await handleDeleteUser(deleteConfirmItem.item)
    } else {
      await handleDeleteRole(deleteConfirmItem.item)
    }
    setDeleteConfirmItem(null)
  }

  // Remove user membership from role (PostgreSQL only)
  const handleRemoveUserMembership = async (username: string, roleName: string) => {
    if (!connectionId || !selectedDatabase) return

    try {
      await AgentProxyService.removeUserMembership(connectionId, selectedDatabase, username, roleName)
      showToast('success', `Removed ${username} from role ${roleName}`)
      
      // Refresh user details to show updated memberships
      if (selectedUser && selectedUser.user_name === username) {
        void loadCurrentUserDeps()
      }
      
      setShowRemoveMembershipModal(false)
    } catch (error) {
      console.error('Failed to remove user membership:', error)
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      showToast('error', 'Failed to remove user membership', errorMessage)
    }
  }

  // Reassign owned objects from one user to another (PostgreSQL only)
  const handleReassignOwnedObjects = async (fromUsername: string, toUsername: string) => {
    if (!connectionId || !selectedDatabase) return

    try {
      await AgentProxyService.reassignOwnedBy(connectionId, selectedDatabase, fromUsername, toUsername)
      showToast('success', `Reassigned objects from ${fromUsername} to ${toUsername}`)
      
      // Refresh user details to show updated owned objects
      if (selectedUser && selectedUser.user_name === fromUsername) {
        void loadCurrentUserDeps()
      }
      
      setShowReassignOwnedModal(false)
    } catch (error) {
      console.error('Failed to reassign owned objects:', error)
      const errorMessage = error instanceof Error ? error.message : 'An error occurred'
      showToast('error', 'Failed to reassign owned objects', errorMessage)
    }
  }
  

  if (isLoadingConnection) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!dataSource) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">Connection not found</p>
          <Link to="/connections" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
            Back to Connections
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <UserIcon className="w-5 h-5 text-primary-600" />
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                User & Role Management
              </h1>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                • {dataSource.displayName} • {dataSource.databaseType}
              </span>
            </div>
          </div>

          {/* Database Selection */}
          <div className="mt-4 flex items-center space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Database
              </label>
              <select
                value={selectedDatabase}
                onChange={(e) => handleDatabaseChange(e.target.value)}
                className="w-full md:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={isLoadingDatabases}
              >
                <option value="">Choose a database...</option>
                {databasesList.map((db) => (
                  <option key={db.name} value={db.name}>
                    {db.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        {selectedDatabase && (
          <div className="px-6">
            <div className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setActiveTab('users')}
                className={clsx(
                  'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === 'users'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                )}
              >
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-4 h-4" />
                  <span>Users</span>
                </div>
              </button>
              {isPostgreSQL && (
                <button
                  type="button"
                  onClick={() => setActiveTab('roles')}
                  className={clsx(
                    'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                    activeTab === 'roles'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <UserGroupIcon className="w-4 h-4" />
                    <span>Roles</span>
                  </div>
                </button>
              )}
              <button
                type="button"
                onClick={() => setActiveTab('privileges')}
                className={clsx(
                  'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                  activeTab === 'privileges'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                )}
              >
                <div className="flex items-center space-x-2">
                  <ShieldCheckIcon className="w-4 h-4" />
                  <span>Privileges</span>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      {!selectedDatabase ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Database Selected
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Choose a database from the dropdown above to manage users and roles.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <UsersTab
              users={filteredUsers}
              selectedUser={selectedUser}
              isLoading={isLoading}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSelectUser={setSelectedUser}
              onCreateUser={() => setShowCreateUserModal(true)}
              onEditUser={() => setShowEditUserModal(true)}
              onDeleteUser={(user) => setDeleteConfirmItem({ type: 'user', item: user })}
              onResetPassword={() => setShowPasswordModal(true)}
              userGrants={userGrants}
              isMySQL={isMySQL}
              isPostgreSQL={isPostgreSQL}
              connectionId={connectionId || ''}
              databaseName={selectedDatabase}
              onRefreshUsers={loadUsers}
              deletionDepsByUser={deletionDepsByUser}
              userDeps={currentUserDeps}
              onRemoveMembership={() => setShowRemoveMembershipModal(true)}
              onReassignOwned={() => setShowReassignOwnedModal(true)}
            />
          )}

          {/* Roles Tab */}
          {activeTab === 'roles' && isPostgreSQL && (
            <RolesTab
              roles={filteredRoles}
              selectedRole={selectedRole}
              isLoading={isLoading}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onSelectRole={setSelectedRole}
              onCreateRole={() => setShowCreateRoleModal(true)}
              onEditRole={() => setShowEditRoleModal(true)}
              onDeleteRole={(role) => setDeleteConfirmItem({ type: 'role', item: role })}
              roleGrants={roleGrants}
            />
          )}

          {/* Privileges Tab */}
          {activeTab === 'privileges' && (
            <PrivilegesTab
              users={users}
              roles={roles}
              selectedUser={selectedUser}
              selectedRole={selectedRole}
              userGrants={userGrants}
              roleGrants={roleGrants}
              isLoadingUserGrants={isLoadingUserGrants}
              isLoadingRoleGrants={isLoadingRoleGrants}
              onGrantPrivileges={() => setShowGrantPrivilegesModal(true)}
              onRevokePrivileges={() => setShowRevokePrivilegesModal(true)}
              onUserSelect={setSelectedUser}
              onRoleSelect={setSelectedRole}
              isPostgreSQL={isPostgreSQL}
              connectionId={connectionId!}
              databaseName={selectedDatabase}
            />
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateUserModal && connectionId && (
        <CreateUserModal
          connectionId={connectionId}
          databaseName={selectedDatabase}
          isMySQL={isMySQL}
          isPostgreSQL={isPostgreSQL}
          onClose={() => { setShowCreateUserModal(false) }}
          onSuccess={() => {
            setShowCreateUserModal(false)
            void loadUsers()
          }}
        />
      )}

      {showEditUserModal && selectedUser && connectionId && (
        <EditUserModal
          connectionId={connectionId}
          databaseName={selectedDatabase}
          user={selectedUser}
          isMySQL={isMySQL}
          isPostgreSQL={isPostgreSQL}
          onClose={() => { setShowEditUserModal(false) }}
          onSuccess={() => {
            setShowEditUserModal(false)
            void loadUsers()
          }}
        />
      )}

      {showPasswordModal && selectedUser && connectionId && (
        <ResetPasswordModal
          connectionId={connectionId}
          databaseName={selectedDatabase}
          user={selectedUser}
          onClose={() => { setShowPasswordModal(false) }}
          onSuccess={() => {
            setShowPasswordModal(false)
            showToast('success', 'Password reset successfully')
          }}
        />
      )}

      {showCreateRoleModal && connectionId && isPostgreSQL && (
        <CreateRoleModal
          connectionId={connectionId}
          databaseName={selectedDatabase}
          onClose={() => { setShowCreateRoleModal(false) }}
          onSuccess={() => {
            setShowCreateRoleModal(false)
            void loadRoles()
          }}
        />
      )}

      {showEditRoleModal && selectedRole && connectionId && isPostgreSQL && (
        <EditRoleModal
          connectionId={connectionId}
          databaseName={selectedDatabase}
          role={selectedRole}
          onClose={() => { setShowEditRoleModal(false) }}
          onSuccess={() => {
            setShowEditRoleModal(false)
            void loadRoles()
          }}
        />
      )}

      {showGrantPrivilegesModal && connectionId && (
        <GrantPrivilegesModal
          isOpen={showGrantPrivilegesModal}
          onClose={() => { setShowGrantPrivilegesModal(false) }}
          grantee={selectedUser?.user_name || selectedRole?.role_name || null}
          granteeType={selectedUser ? 'user' : 'role'}
          databaseName={selectedDatabase}
          connectionId={connectionId}
          isPostgreSQL={isPostgreSQL}
          mysqlHost={isMySQL ? (selectedUser?.host || '%') : undefined}
          onSuccess={() => {
            setShowGrantPrivilegesModal(false)
            if (selectedUser) void loadUserGrants(selectedUser.user_name!, isMySQL ? (selectedUser.host || '%') : undefined)
            if (selectedRole) void loadRoleGrants(selectedRole.role_name)
          }}
        />
      )}

      {showRevokePrivilegesModal && connectionId && (
        <RevokePrivilegesModal
          isOpen={showRevokePrivilegesModal}
          onClose={() => { setShowRevokePrivilegesModal(false) }}
          grantee={selectedUser?.user_name || selectedRole?.role_name || null}
          granteeType={selectedUser ? 'user' : 'role'}
          currentGrants={selectedUser ? (
            // Convert dependency privileges to Grant format
            currentUserDeps ? [
              // Schema privileges
              ...currentUserDeps.grants.schema_privileges.flatMap(sp => 
                sp.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'schema' as const,
                  object_name: sp.schema_name,
                  schema_name: sp.schema_name,
                  source: sp.source,
                  grant_option: false
                }))
              ),
              // Sequence privileges
              ...currentUserDeps.grants.sequence_privileges.flatMap(seq => 
                seq.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'sequence' as const,
                  object_name: seq.sequence_full_name || seq.sequence_name,
                  schema_name: seq.schema_name,
                  source: seq.source,
                  grant_option: seq.is_grantable
                }))
              ),
              // Table privileges
              ...currentUserDeps.grants.table_privileges.flatMap(tp => 
                tp.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'table' as const,
                  object_name: tp.table_name,
                  schema_name: tp.schema_name,
                  source: tp.source,
                  grant_option: false
                }))
              ),
              // Function privileges
              ...currentUserDeps.grants.function_privileges.flatMap(fp => 
                fp.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'function' as const,
                  object_name: fp.function_signature || fp.function_name,
                  schema_name: fp.schema_name,
                  source: fp.source,
                  grant_option: fp.is_grantable
                }))
              ),
              // Column privileges
              ...currentUserDeps.grants.column_privileges.flatMap(cp => 
                cp.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'column' as const,
                  object_name: `${cp.table_name}.${cp.column_name}`,
                  schema_name: cp.schema_name,
                  source: cp.source,
                  grant_option: false
                }))
              ),
              // Database privileges
              ...currentUserDeps.grants.database_privileges.flatMap(dp => 
                dp.privileges.map(priv => ({
                  privilege: priv,
                  object_type: 'database' as const,
                  object_name: dp.database_name,
                  source: dp.source,
                  grant_option: false
                }))
              ),
              // Global privileges (MySQL)
              ...(((currentUserDeps as unknown as { grants?: { global_privileges?: { privileges?: string[]; with_grant_option?: boolean; source?: string }[] } })?.grants?.global_privileges) || []).flatMap((gp: { privileges?: string[]; with_grant_option?: boolean; source?: string }) => 
                (Array.isArray(gp.privileges) ? gp.privileges : []).map((priv) => ({
                  privilege: priv,
                  object_type: 'global' as const,
                  object_name: '*.*',
                  schema_name: undefined,
                  source: gp.source,
                  grant_option: !!gp.with_grant_option
                }))
              ),
              // Add legacy userGrants if any exist
              ...userGrants
            ] : userGrants
          ) : roleGrants}
          databaseName={selectedDatabase}
          connectionId={connectionId}
          isPostgreSQL={isPostgreSQL}
          mysqlHost={isMySQL ? (selectedUser?.host || '%') : undefined}
          onSuccess={() => {
            setShowRevokePrivilegesModal(false)
            // Reload user dependencies to update the UI with latest privileges
            if (selectedUser) {
              void loadCurrentUserDeps()
              void loadUserGrants(selectedUser.user_name!, isMySQL ? (selectedUser.host || '%') : undefined)
            }
            if (selectedRole) void loadRoleGrants(selectedRole.role_name)
          }}
        />
      )}

      {/* Remove Membership Modal */}
      {showRemoveMembershipModal && selectedUser && connectionId && isPostgreSQL && currentUserDeps && (
        <RemoveMembershipModal
          isOpen={showRemoveMembershipModal}
          onClose={() => { setShowRemoveMembershipModal(false) }}
          username={selectedUser.user_name!}
          memberRoles={currentUserDeps.memberOfRoles || []}
          onConfirm={handleRemoveUserMembership}
        />
      )}

      {/* Reassign Owned Objects Modal */}
      {showReassignOwnedModal && selectedUser && connectionId && isPostgreSQL && (
        <ReassignOwnedObjectsModal
          isOpen={showReassignOwnedModal}
          onClose={() => { setShowReassignOwnedModal(false) }}
          fromUsername={selectedUser.user_name!}
          availableUsers={users.filter(u => u.user_name !== selectedUser.user_name).map(u => u.user_name!)}
          onConfirm={handleReassignOwnedObjects}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteConfirmItem}
        title={`Delete ${deleteConfirmItem?.type === 'user' ? 'User' : 'Role'}`}
        message={`Are you sure you want to delete ${
          deleteConfirmItem?.type === 'user' 
            ? `user "${deleteConfirmItem.item.user_name}"`
            : `role "${deleteConfirmItem?.item.role_name}"`
        }? This action cannot be undone.`}
        confirmText="Delete"
        isDangerous
        onConfirm={() => void confirmDelete()}
        onClose={() => { setDeleteConfirmItem(null) }}
      />
    </div>
  )
}

// Users Tab Component
interface UsersTabProps {
  users: DatabaseUser[]
  selectedUser: DatabaseUser | null
  isLoading: boolean
  searchTerm: string
  onSearchChange: (term: string) => void
  onSelectUser: (user: DatabaseUser | null) => void
  onCreateUser: () => void
  onEditUser: () => void
  onDeleteUser: (user: DatabaseUser) => void
  onResetPassword: () => void
  userGrants: Grant[]
  isMySQL: boolean
  isPostgreSQL: boolean
  connectionId: string
  databaseName: string
  onRefreshUsers: () => void
  deletionDepsByUser?: Record<string, unknown>
  userDeps?: {
    memberOfRoles: string[]
    grantedRoles: string[]
    defaultPrivilegesCount: number
    owned: { 
      schemas: number
      relations: number // includes tables, views, materialized views, sequences
      databases: number
      functions: number
      types: number
      languages: number
      extensions: number
      collations: number
      conversions: number
      foreign_servers: number
      foreign_data_wrappers: number
    }
    ownedDetails: {
      databases: string[]
      schemas: string[]
      relations: string[]
      functions: string[]
      types: string[]
      languages: string[]
      extensions: string[]
      collations: string[]
      conversions: string[]
      foreign_servers: string[]
      foreign_data_wrappers: string[]
    }
    memberships: {
      is_member_of: string[]
      has_members: string[]
    }
    grants: {
      summary: {
        username: string
        database_type: string
        total_privileges: number
        privilege_categories: {
          read_privileges: number
          write_privileges: number
          admin_privileges: number
          inherited_privileges: number
        }
      } | null
      role_memberships: {
        role_name: string
        admin_option: boolean
        role_privileges: {
          superuser: boolean
          create_database: boolean
          create_role: boolean
        }
        source: string
      }[]
      database_privileges: {
        database_name: string
        privileges: string[]
        object_type: string
        source: string
      }[]
      schema_privileges: {
        database_name: string
        schema_name: string
        privileges: string[]
        source: string
      }[]
      table_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        privileges: string[]
        source: string
      }[]
      column_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        column_name: string
        privileges: string[]
        source: string
      }[]
      function_privileges: {
        schema_name: string
        function_name: string
        function_signature: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[]
      sequence_privileges: {
        schema_name: string
        sequence_name: string
        sequence_full_name: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[],
      effective_permissions: {
        can_read_objects: string[]
        can_write_objects: string[]
        can_create_in: string[]
        can_delete_from: string[]
        admin_objects: string[]
      }
    }
  } | null
  onRemoveMembership: () => void
  onReassignOwned: () => void
}

function UsersTab({
  users,
  selectedUser,
  isLoading,
  searchTerm,
  onSearchChange,
  onSelectUser,
  onCreateUser,
  onEditUser,
  onDeleteUser,
  onResetPassword,
  userGrants,
  isMySQL,
  isPostgreSQL,
  connectionId,
  databaseName,
  onRefreshUsers,
  deletionDepsByUser,
  userDeps,
  onRemoveMembership,
  onReassignOwned,
}: UsersTabProps) {
  // Multi-select state
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [lastClickedUser, setLastClickedUser] = useState<string | null>(null)
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const { showToast } = useToast()

  // Dependency insight state (PostgreSQL only)
  const [isLoadingDeps, setIsLoadingDeps] = useState(false)
  const [deps, setDeps] = useState<{
    memberOfRoles: string[]
    grantedRoles: string[]
    defaultPrivilegesCount: number
    owned: { 
      schemas: number
      relations: number // includes tables, views, materialized views, sequences
      databases: number
      functions: number
      types: number
      languages: number
      extensions: number
      collations: number
      conversions: number
      foreign_servers: number
      foreign_data_wrappers: number
    }
    ownedDetails: {
      databases: string[]
      schemas: string[]
      relations: string[]
      functions: string[]
      types: string[]
      languages: string[]
      extensions: string[]
      collations: string[]
      conversions: string[]
      foreign_servers: string[]
      foreign_data_wrappers: string[]
    }
    memberships: {
      is_member_of: string[]
      has_members: string[]
    }
    grants: {
      summary: {
        username: string
        database_type: string
        total_privileges: number
        privilege_categories: {
          read_privileges: number
          write_privileges: number
          admin_privileges: number
          inherited_privileges: number
        }
      } | null
      role_memberships: {
        role_name: string
        admin_option: boolean
        role_privileges: {
          superuser: boolean
          create_database: boolean
          create_role: boolean
        }
        source: string
      }[]
      database_privileges: {
        database_name: string
        privileges: string[]
        object_type: string
        source: string
      }[]
      schema_privileges: {
        database_name: string
        schema_name: string
        privileges: string[]
        source: string
      }[]
      table_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        privileges: string[]
        source: string
      }[]
      column_privileges: {
        database_name: string
        schema_name: string
        table_name: string
        column_name: string
        privileges: string[]
        source: string
      }[]
      function_privileges: {
        schema_name: string
        function_name: string
        function_signature: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[]
      sequence_privileges: {
        schema_name: string
        sequence_name: string
        sequence_full_name: string
        privilege_type: string
        privileges: string[]
        is_grantable: boolean
        grantor: string
        object_type: string
        source: string
      }[],
      effective_permissions: {
        can_read_objects: string[]
        can_write_objects: string[]
        can_create_in: string[]
        can_delete_from: string[]
        admin_objects: string[]
      }
    }
  } | null>(null)


  // Helper function to create user key for selection
  const createUserKey = useCallback((user: DatabaseUser) => {
    return `${user.user_name}@${user.host || 'localhost'}`
  }, [])

  // Multi-select handlers
  const handleUserClick = useCallback((user: DatabaseUser, event?: React.MouseEvent) => {
    if (event && (event.ctrlKey || event.metaKey)) {
      // Ctrl+click: toggle individual selection
      const userKey = createUserKey(user)
      setSelectedUsers(prev => {
        const newSelection = new Set(prev)
        if (newSelection.has(userKey)) {
          newSelection.delete(userKey)
        } else {
          newSelection.add(userKey)
        }
        return newSelection
      })
      setLastClickedUser(userKey)
    } else if (event && event.shiftKey && lastClickedUser) {
      // Shift+click: range selection
      const userKey = createUserKey(user)
      const currentIndex = users.findIndex(u => createUserKey(u) === userKey)
      const lastIndex = users.findIndex(u => createUserKey(u) === lastClickedUser)
      
      if (currentIndex !== -1 && lastIndex !== -1) {
        const start = Math.min(currentIndex, lastIndex)
        const end = Math.max(currentIndex, lastIndex)
        
        setSelectedUsers(prev => {
          const newSelection = new Set(prev)
          for (let i = start; i <= end; i++) {
            newSelection.add(createUserKey(users[i]))
          }
          return newSelection
        })
      }
    } else {
      // Regular click: select this user for details view
      onSelectUser(user)
    }
  }, [users, lastClickedUser, createUserKey, onSelectUser])

  // Bulk delete functionality
  const handleBulkDelete = useCallback(async () => {
    if (!connectionId || !databaseName || selectedUsers.size === 0) return
    
    const selectedUsersList = users.filter(user => 
      selectedUsers.has(createUserKey(user))
    )

    let successCount = 0
    let failCount = 0
    
    for (const user of selectedUsersList) {
      try {
        await AgentProxyService.dropUser(connectionId, databaseName, user.user_name || '')
        successCount++
      } catch (error) {
        failCount++
        console.error(`Failed to delete user ${user.user_name}:`, error)
      }
    }
    
    if (successCount > 0) {
      showToast('success', `Successfully deleted ${successCount} user${successCount > 1 ? 's' : ''}`, '')
    }
    if (failCount > 0) {
      showToast('error', `Failed to delete ${failCount} user${failCount > 1 ? 's' : ''}`, '')
    }
    
    setSelectedUsers(new Set())
    setLastClickedUser(null)
    onSelectUser(null)
    onRefreshUsers()
    setShowBulkDeleteConfirm(false)
  }, [selectedUsers, users, connectionId, databaseName, createUserKey, showToast, onRefreshUsers, onSelectUser])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return
      
      if (event.key === 'Delete' && selectedUsers.size > 0) {
        event.preventDefault()
        setShowBulkDeleteConfirm(true)
      } else if (event.key === 'Escape') {
        setSelectedUsers(new Set())
        setLastClickedUser(null)
        onSelectUser(null)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedUsers.size, onSelectUser])

  // Clear selections when users list changes
  useEffect(() => {
    setSelectedUsers(new Set())
    setLastClickedUser(null)
  }, [databaseName, users.length])

  // Load dependency information for selected user (PostgreSQL)
  useEffect(() => {
    const loadDeps = async () => {
      if (!isPostgreSQL || !selectedUser?.user_name || !connectionId || !databaseName) {
        setDeps(null)
        return
      }
      setIsLoadingDeps(true)
      try {
        // Prefer back-end dependency diagnostics captured on last delete attempt (most accurate)
        const prior = deletionDepsByUser?.[selectedUser.user_name]
        if (prior && (typeof prior === 'object' || typeof prior === 'string')) {
          try {
            const parsed: unknown = typeof prior === 'string' ? JSON.parse(prior) : prior
            // Attempt to convert the server dependency payload into our summary counts
            const ownedObj = (parsed && typeof parsed === 'object' && 'owned_objects' in parsed)
              ? (parsed as { owned_objects?: unknown }).owned_objects
              : undefined
            const ownedCounts = { schemas: 0, relations: 0 }
            if (ownedObj && typeof ownedObj === 'object' && !Array.isArray(ownedObj)) {
              const readCount = (name: string) => {
                const v = (ownedObj as Record<string, unknown>)[name]
                return Array.isArray(v) ? v.length : 0
              }
              ownedCounts.schemas = readCount('schemas')
              // Relations includes tables, views, materialized views, and sequences
              ownedCounts.relations = readCount('relations')
              // Legacy support: Add individual counts if they exist
              if ('tables' in (ownedObj as Record<string, unknown>) && !('relations' in (ownedObj as Record<string, unknown>))) {
                ownedCounts.relations += readCount('tables')
                ownedCounts.relations += readCount('views')
                ownedCounts.relations += readCount('sequences')
              }
            }
            const memberships = (parsed && typeof parsed === 'object' && 'memberships' in parsed && (parsed as { memberships?: unknown }).memberships && typeof (parsed as { memberships?: unknown }).memberships === 'object')
              ? (((parsed as { memberships?: { is_member_of?: unknown[] } }).memberships as { is_member_of?: unknown[] }).is_member_of ?? [])
              : []
            setDeps({
              memberOfRoles: Array.isArray(memberships) ? memberships.filter((m): m is string => typeof m === 'string') : [],
              grantedRoles: [],
              defaultPrivilegesCount: 0,
              owned: { 
                ...ownedCounts, 
                databases: 0, functions: 0, types: 0, languages: 0, 
                extensions: 0, collations: 0, conversions: 0, 
                foreign_servers: 0, foreign_data_wrappers: 0 
              },
              ownedDetails: {
                databases: [], schemas: [], relations: [], functions: [], types: [],
                languages: [], extensions: [], collations: [], conversions: [],
                foreign_servers: [], foreign_data_wrappers: []
              },
              memberships: {
                is_member_of: Array.isArray(memberships) ? memberships.filter((m): m is string => typeof m === 'string') : [],
                has_members: []
              },
              grants: {
                summary: null,
                role_memberships: [],
                database_privileges: [],
            schema_privileges: [],
            table_privileges: [],
            column_privileges: [],
            function_privileges: [],
            sequence_privileges: [],
                effective_permissions: {
                  can_read_objects: [],
                  can_write_objects: [],
                  can_create_in: [],
                  can_delete_from: [],
                  admin_objects: []
                }
              }
            })
          } catch {
            // If parsing fails, fall back to live queries below
          }
        }

        const username = selectedUser.user_name
        const safeUserLiteral = username.replace(/'/g, "''")
        const ownedQuery = `SELECT
  (SELECT count(*) FROM pg_namespace n JOIN pg_roles r ON r.oid = n.nspowner WHERE r.rolname = '${safeUserLiteral}') AS schemas,
  (SELECT count(*) FROM pg_class c JOIN pg_roles r ON r.oid = c.relowner WHERE r.rolname = '${safeUserLiteral}' AND c.relkind IN ('r','p','t','f')) AS tables,
  (SELECT count(*) FROM pg_class c JOIN pg_roles r ON r.oid = c.relowner WHERE r.rolname = '${safeUserLiteral}' AND c.relkind = 'v') AS views,
  (SELECT count(*) FROM pg_class c JOIN pg_roles r ON r.oid = c.relowner WHERE r.rolname = '${safeUserLiteral}' AND c.relkind = 'S') AS sequences;`

        const [detailsResp, defaultsResp, ownedResp] = await Promise.all([
          AgentProxyService.getUserDetails(connectionId, databaseName, username, true), // Include dependencies
          AgentProxyService.getDefaultPrivileges(connectionId, databaseName),
          AgentProxyService.executeQuery(connectionId, ownedQuery, databaseName, true)
        ])

        // Extract roles info from enhanced response
        let memberOfRoles: string[] = []
        let grantedRoles: string[] = []
        let enhancedOwnedCounts: { schemas: number; relations: number } | null = null

        if (detailsResp && typeof detailsResp === 'object') {
          const anyResp = detailsResp as Record<string, unknown>
          const userDetails = (anyResp.user_details || {}) as Record<string, unknown>
          
          // Try to get enhanced dependency information first
          if ('dependencies' in userDetails) {
            const deps = userDetails.dependencies as Record<string, unknown>
            
            // Extract memberships from dependencies
            if (deps.memberships && typeof deps.memberships === 'object') {
              const memberships = deps.memberships as { is_member_of?: unknown[]; has_members?: unknown[] }
              memberOfRoles = Array.isArray(memberships.is_member_of) ? memberships.is_member_of as string[] : []
              grantedRoles = Array.isArray(memberships.has_members) ? memberships.has_members as string[] : []
            }
            
            // Extract owned object counts from dependencies
            if (deps.owned_counts && typeof deps.owned_counts === 'object') {
              const oc = deps.owned_counts as Record<string, unknown>
              const getNum = (k: string) => (typeof oc[k] === 'number' ? oc[k] : 0)
              enhancedOwnedCounts = {
                schemas: getNum('schemas'),
                // Combine all relation types
                relations: getNum('relations') + getNum('tables') + getNum('views') + getNum('sequences')
              }
            }
          }
          
          // Fallback to original logic if enhanced data not available
          if (memberOfRoles.length === 0) {
            const topMember = Array.isArray((anyResp as { member_of_roles?: unknown[] }).member_of_roles) ? (anyResp as { member_of_roles?: unknown[] }).member_of_roles as string[] : []
            const topGranted = Array.isArray((anyResp as { granted_roles?: unknown[] }).granted_roles) ? (anyResp as { granted_roles?: unknown[] }).granted_roles as string[] : []
            const nestedMember = Array.isArray((userDetails as { member_of_roles?: unknown[] }).member_of_roles) ? (userDetails as { member_of_roles?: unknown[] }).member_of_roles as string[] : []
            const nestedGranted = Array.isArray((userDetails as { granted_roles?: unknown[] }).granted_roles) ? (userDetails as { granted_roles?: unknown[] }).granted_roles as string[] : []
            memberOfRoles = Array.isArray(topMember) && topMember.length > 0 ? topMember : Array.isArray(nestedMember) ? nestedMember : []
            grantedRoles = Array.isArray(topGranted) && topGranted.length > 0 ? topGranted : Array.isArray(nestedGranted) ? nestedGranted : []
          }
        }

        // Default privileges count
        let defaultPrivilegesCount = 0
        if (defaultsResp && typeof defaultsResp === 'object' && 'default_privileges' in (defaultsResp as Record<string, unknown>)) {
          const list = (defaultsResp as { default_privileges?: unknown[] }).default_privileges || []
          if (Array.isArray(list)) defaultPrivilegesCount = list.length
        }

        // Owned objects summary - use enhanced data if available, otherwise fallback to query
        const owned = enhancedOwnedCounts || { schemas: 0, relations: 0 }
        if (!enhancedOwnedCounts && ownedResp && typeof ownedResp === 'object' && 'rows' in (ownedResp as { rows?: unknown })) {
          const rowsUnknown = (ownedResp as { rows?: unknown }).rows
          const rows = Array.isArray(rowsUnknown) ? rowsUnknown : []
          const first = (rows[0] && typeof rows[0] === 'object' && !Array.isArray(rows[0])) ? (rows[0] as Record<string, unknown>) : {}
          const readInt = (v: unknown) => (typeof v === 'number' ? v : Number.isFinite(Number(v)) ? Number(v) : 0)
          const getNum = (obj: Record<string, unknown>, key: string) => readInt(obj[key])
          owned.schemas = getNum(first, 'schemas')
          // Combine all relation types into relations count
          const tables = getNum(first, 'tables')
          const views = getNum(first, 'views')
          const sequences = getNum(first, 'sequences')
          owned.relations = tables + views + sequences
        }

        setDeps({ 
          memberOfRoles, 
          grantedRoles, 
          defaultPrivilegesCount, 
          owned: {
            ...owned,
            databases: 0, functions: 0, types: 0, languages: 0, 
            extensions: 0, collations: 0, conversions: 0, 
            foreign_servers: 0, foreign_data_wrappers: 0
          },
          ownedDetails: {
            databases: [], schemas: [], relations: [], functions: [], types: [],
            languages: [], extensions: [], collations: [], conversions: [],
            foreign_servers: [], foreign_data_wrappers: []
          },
          memberships: {
            is_member_of: memberOfRoles,
            has_members: grantedRoles
          },
          grants: {
            summary: null,
            role_memberships: [],
            database_privileges: [],
            schema_privileges: [],
            table_privileges: [],
            column_privileges: [],
            function_privileges: [],
            sequence_privileges: [],
            effective_permissions: {
              can_read_objects: [],
              can_write_objects: [],
              can_create_in: [],
              can_delete_from: [],
              admin_objects: []
            }
          }
        })
      } catch (err) {
        console.warn('Failed to load dependency info for user:', err)
        setDeps(null)
      } finally {
        setIsLoadingDeps(false)
      }
    }

    void loadDeps()
  }, [isPostgreSQL, selectedUser?.user_name, connectionId, databaseName, deletionDepsByUser])


  return (
    <div ref={containerRef} className="flex h-full min-h-0" tabIndex={0}>
      {/* Users List */}
      <div className="w-96 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Database Users</h2>
            <div className="flex items-center space-x-2">
              {selectedUsers.size > 0 && (
                <button
                  type="button"
                  onClick={() => { setShowBulkDeleteConfirm(true) }}
                  className="flex items-center px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  <TrashIcon className="w-3 h-3 mr-1" />
                  Delete {selectedUsers.size}
                </button>
              )}
              <button
                type="button"
                onClick={onCreateUser}
                className="flex items-center px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Create User
              </button>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => { onSearchChange(e.target.value) }}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <UserIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No users found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => {
                const userKey = createUserKey(user)
                const isSelected = selectedUsers.has(userKey)
                
                return (
                  <div
                    key={user.user_name}
                    className={clsx(
                      'p-2 cursor-pointer transition-all duration-200 group relative',
                      selectedUser?.user_name === user.user_name && 'bg-primary-50 dark:bg-primary-900/20',
                      isSelected 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-l-2 hover:border-gray-300 dark:hover:border-gray-600'
                    )}
                    onClick={(e) => { handleUserClick(user, e) }}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Modern Custom Checkbox */}
                      <div 
                        className={clsx(
                          'w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 cursor-pointer shrink-0',
                          isSelected 
                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 group-hover:border-blue-400'
                        )}
                        onClick={(e) => {
                          e.stopPropagation()
                          const userKey = createUserKey(user)
                          setSelectedUsers(prev => {
                            const newSelection = new Set(prev)
                            if (newSelection.has(userKey)) {
                              newSelection.delete(userKey)
                            } else {
                              newSelection.add(userKey)
                            }
                            return newSelection
                          })
                        }}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      
                      <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className={clsx(
                            'font-medium truncate text-sm',
                            isSelected 
                              ? 'text-blue-900 dark:text-blue-100' 
                              : 'text-gray-900 dark:text-white'
                          )}>
                            {user.user_name}
                          </span>
                          {user.superuser && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              Super
                            </span>
                          )}
                        </div>
                        {isMySQL && user.host && (
                          <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400 truncate">
                            @{user.host}
                          </p>
                        )}
                      </div>
                      
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* User Details */}
      <div className="flex-1 bg-white dark:bg-gray-800 overflow-y-auto min-h-0">
        {!selectedUser ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a User
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a user from the list to view details and manage permissions.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedUser.user_name}
                  </h2>
                  {isMySQL && selectedUser.host && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedUser.user_name}@{selectedUser.host}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={onResetPassword}
                    className="flex items-center px-3 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    <KeyIcon className="w-4 h-4 mr-1" />
                    Reset Password
                  </button>
                  <button
                    type="button"
                    onClick={onEditUser}
                    className="flex items-center px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Edit
                  </button>
                  {isPostgreSQL && userDeps && userDeps.memberOfRoles.length > 0 && (
                    <button
                      type="button"
                      onClick={onRemoveMembership}
                      className="flex items-center px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                      title="Remove user from roles"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                      Remove Membership
                    </button>
                  )}
                  {isPostgreSQL && userDeps && (userDeps.owned.schemas > 0 || userDeps.owned.relations > 0) && (
                    <button
                      type="button"
                      onClick={onReassignOwned}
                      className="flex items-center px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      title="Reassign owned objects to another user"
                    >
                      <ArrowPathIcon className="w-4 h-4 mr-1" />
                      Reassign Objects
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { onDeleteUser(selectedUser) }}
                    className="flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>

              {/* User Properties */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Properties</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Username
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedUser.user_name}
                    </p>
                  </div>
                  {isMySQL && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                        Host
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {selectedUser.host || 'localhost'}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Can Login
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedUser.canLogin ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Superuser
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedUser.superuser ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

                {/* Enhanced Dependency Insight (PostgreSQL only) */}
                {isPostgreSQL && (
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      <UserGroupIcon className="w-4 h-4 inline mr-2" />
                      Dependency Insight
                    </h3>
                    {isLoadingDeps ? (
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                        Analyzing ownership and role memberships...
                      </div>
                    ) : !deps ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No dependency details available</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Role Memberships */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Role Memberships</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">Member of:</span>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                                {deps.memberships.is_member_of.length > 0 ? (
                                  <span className="inline-flex flex-wrap gap-1">
                                    {deps.memberships.is_member_of.map(role => (
                                      <span key={role} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                                        {role}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  <span className="text-gray-500 dark:text-gray-400">None</span>
                                )}
                              </p>
                            </div>
                            <div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">Has members:</span>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                                {deps.memberships.has_members.length > 0 ? (
                                  <span className="inline-flex flex-wrap gap-1">
                                    {deps.memberships.has_members.map(member => (
                                      <span key={member} className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs">
                                        {member}
                                      </span>
                                    ))}
                                  </span>
                                ) : (
                                  <span className="text-gray-500 dark:text-gray-400">None</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Owned Objects Summary */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Owned Objects</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            {[
                              { key: 'databases', label: 'Databases', icon: '🗄️' },
                              { key: 'schemas', label: 'Schemas', icon: '📋' },
                              { key: 'relations', label: 'Relations', icon: '📊', tooltip: 'Tables, Views, Materialized Views, Sequences' },
                              { key: 'functions', label: 'Functions', icon: '⚙️' },
                              { key: 'types', label: 'Types', icon: '🏷️' },
                              { key: 'extensions', label: 'Extensions', icon: '🔌' },
                              { key: 'languages', label: 'Languages', icon: '🗣️' },
                              { key: 'collations', label: 'Collations', icon: '📝' }
                            ].map(({ key, label, icon, tooltip }) => {
                              const count = (deps.owned as Record<string, number>)[key] || 0
                              return (
                                <div key={key} className={`p-2 rounded ${count > 0 ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-gray-100 dark:bg-gray-800'}`} title={tooltip}>
                                  <div className="flex items-center justify-between">
                                    <span className={`text-xs ${count > 0 ? 'text-yellow-800 dark:text-yellow-200' : 'text-gray-600 dark:text-gray-400'}`}>
                                      {icon} {label}
                                    </span>
                                    <span className={`font-medium ${count > 0 ? 'text-yellow-800 dark:text-yellow-200' : 'text-gray-600 dark:text-gray-400'}`}>
                                      {count}
                                    </span>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Privilege Summary */}
                        {deps.grants.summary && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Privilege Summary</h4>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-blue-800 dark:text-blue-200">📖 Read</span>
                                  <span className="font-medium text-blue-800 dark:text-blue-200">{deps.grants.summary.privilege_categories.read_privileges}</span>
                                </div>
                              </div>
                              <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-green-800 dark:text-green-200">✏️ Write</span>
                                  <span className="font-medium text-green-800 dark:text-green-200">{deps.grants.summary.privilege_categories.write_privileges}</span>
                                </div>
                              </div>
                              <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-purple-800 dark:text-purple-200">⚡ Admin</span>
                                  <span className="font-medium text-purple-800 dark:text-purple-200">{deps.grants.summary.privilege_categories.admin_privileges}</span>
                                </div>
                              </div>
                              <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-orange-800 dark:text-orange-200">🔗 Inherited</span>
                                  <span className="font-medium text-orange-800 dark:text-orange-200">{deps.grants.summary.privilege_categories.inherited_privileges}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}


                        {/* Database Privileges */}
                        {deps.grants.database_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Database Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.database_privileges.map((dbPriv, index) => (
                                <div key={index} className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {dbPriv.database_name}
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {dbPriv.privileges.map((priv, privIndex) => (
                                      <span key={privIndex} className="px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded text-xs">
                                        {priv}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Schema Privileges */}
                        {deps.grants.schema_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Schema Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.schema_privileges.map((schemaPriv, index) => (
                                <div key={index} className="p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {schemaPriv.database_name}.{schemaPriv.schema_name}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {schemaPriv.privileges.length > 0 ? (
                                      schemaPriv.privileges.map((priv, privIndex) => (
                                        <span key={privIndex} className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200 rounded text-xs">
                                          {priv}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs italic">
                                        No explicit privileges
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Source: {schemaPriv.source}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Table Privileges */}
                        {deps.grants.table_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Table Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.table_privileges.map((tablePriv, index) => (
                                <div key={index} className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {tablePriv.database_name}.{tablePriv.schema_name}.{tablePriv.table_name}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {tablePriv.privileges.map((priv, privIndex) => (
                                      <span key={privIndex} className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200 rounded text-xs">
                                        {priv}
                                      </span>
                                    ))}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Source: {tablePriv.source}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Column Privileges */}
                        {deps.grants.column_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Column Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.column_privileges.map((colPriv, index) => (
                                <div key={index} className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {colPriv.database_name}.{colPriv.schema_name}.{colPriv.table_name}.{colPriv.column_name}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {colPriv.privileges.map((priv, privIndex) => (
                                      <span key={privIndex} className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded text-xs">
                                        {priv}
                                      </span>
                                    ))}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Source: {colPriv.source}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Function Privileges */}
                        {deps.grants.function_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Function Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.function_privileges.map((funcPriv, index) => (
                                <div key={index} className="p-2 bg-violet-50 dark:bg-violet-900/20 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {funcPriv.schema_name}.{funcPriv.function_name}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                    {funcPriv.function_signature}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {/* Use privilege_type if privileges array is empty */}
                                    {funcPriv.privileges.length > 0 ? (
                                      funcPriv.privileges.map((priv, privIndex) => (
                                        <span key={privIndex} className="px-1.5 py-0.5 bg-violet-100 dark:bg-violet-800 text-violet-800 dark:text-violet-200 rounded text-xs">
                                          {priv}
                                        </span>
                                      ))
                                    ) : funcPriv.privilege_type ? (
                                      <span className="px-1.5 py-0.5 bg-violet-100 dark:bg-violet-800 text-violet-800 dark:text-violet-200 rounded text-xs">
                                        {funcPriv.privilege_type}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Source: {funcPriv.source} {funcPriv.is_grantable && '(Grantable)'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Sequence Privileges */}
                        {deps.grants.sequence_privileges.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Sequence Privileges</h4>
                            <div className="max-h-32 overflow-y-auto space-y-2">
                              {deps.grants.sequence_privileges.map((seqPriv, index) => (
                                <div key={index} className="p-2 bg-pink-50 dark:bg-pink-900/20 rounded text-xs">
                                  <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                    {seqPriv.sequence_full_name}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    {/* Use privilege_type if privileges array is empty */}
                                    {seqPriv.privileges.length > 0 ? (
                                      seqPriv.privileges.map((priv, privIndex) => (
                                        <span key={privIndex} className="px-1.5 py-0.5 bg-pink-100 dark:bg-pink-800 text-pink-800 dark:text-pink-200 rounded text-xs">
                                          {priv}
                                        </span>
                                      ))
                                    ) : seqPriv.privilege_type ? (
                                      <span className="px-1.5 py-0.5 bg-pink-100 dark:bg-pink-800 text-pink-800 dark:text-pink-200 rounded text-xs">
                                        {seqPriv.privilege_type}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Source: {seqPriv.source} {seqPriv.is_grantable && '(Grantable)'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Effective Permissions */}
                        {deps.grants.effective_permissions && Object.keys(deps.grants.effective_permissions).length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Effective Permissions</h4>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {Object.entries(deps.grants.effective_permissions).map(([objType, objects]) => {
                                if (Array.isArray(objects) && objects.length > 0) {
                                  return (
                                    <div key={objType} className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded">
                                      <div className="flex items-center justify-between">
                                        <span className="text-teal-800 dark:text-teal-200 capitalize">
                                          {objType.replace('_', ' ')}
                                        </span>
                                        <span className="font-medium text-teal-800 dark:text-teal-200">{objects.length}</span>
                                      </div>
                                    </div>
                                  )
                                }
                                return null
                              })}
                            </div>
                          </div>
                        )}

                        {/* Default Privileges */}
                        {deps.defaultPrivilegesCount > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Default Privileges</h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full">
                                {deps.defaultPrivilegesCount} entries
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Warning Note */}
                        {(deps.owned.databases + deps.owned.schemas + deps.owned.relations + deps.owned.functions + deps.owned.types + deps.owned.extensions > 0) && (
                          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-xs text-amber-800 dark:text-amber-200">
                              ⚠️ <strong>Note:</strong> This user owns database objects. You may need to reassign ownership or drop these objects before deleting the user.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* User Grants */}
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Privileges & Grants
                </h3>
                {!userGrants || !Array.isArray(userGrants) || userGrants.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No privileges granted</p>
                ) : (
                  <div className="space-y-2">
                    {userGrants.map((grant, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {grant.privilege}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            on {grant.object_type} {grant.object_type === 'table' && grant.schema_name ? `${grant.schema_name}.` : ''}{grant.object_name}
                          </span>
                        </div>
                        {grant.grant_option && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            WITH GRANT OPTION
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => { setShowBulkDeleteConfirm(false) }}
        onConfirm={handleBulkDelete}
        title="Delete Selected Users"
        message={`Are you sure you want to delete ${selectedUsers.size} selected user${selectedUsers.size > 1 ? 's' : ''}? This action cannot be undone and will permanently remove the user account${selectedUsers.size > 1 ? 's' : ''} from the database.`}
        confirmText="Delete Users"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}

// Roles Tab Component (PostgreSQL only)
interface RolesTabProps {
  roles: Role[]
  selectedRole: Role | null
  isLoading: boolean
  searchTerm: string
  onSearchChange: (term: string) => void
  onSelectRole: (role: Role | null) => void
  onCreateRole: () => void
  onEditRole: () => void
  onDeleteRole: (role: Role) => void
  roleGrants: Grant[]
}

function RolesTab({
  roles,
  selectedRole,
  isLoading,
  searchTerm,
  onSearchChange,
  onSelectRole,
  onCreateRole,
  onEditRole,
  onDeleteRole,
  roleGrants,
}: RolesTabProps) {
  return (
    <div className="flex h-full min-h-0">
      {/* Roles List */}
      <div className="w-96 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">Database Roles</h2>
            <button
              type="button"
              onClick={onCreateRole}
              className="flex items-center px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Create Role
            </button>
          </div>
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => { onSearchChange(e.target.value) }}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            </div>
          ) : roles.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <UserGroupIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No roles found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {roles.map((role) => (
                <div
                  key={role.role_name}
                  className={clsx(
                    'p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                    selectedRole?.role_name === role.role_name && 'bg-primary-50 dark:bg-primary-900/20'
                  )}
                  onClick={() => { onSelectRole(role) }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <UserGroupIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {role.role_name}
                        </span>
                        {role.superuser && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            Super
                          </span>
                        )}
                        {role.with_login && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Login
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Role Details */}
      <div className="flex-1 bg-white dark:bg-gray-800 overflow-y-auto min-h-0">
        {!selectedRole ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select a Role
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Choose a role from the list to view details and manage permissions.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedRole.role_name}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={onEditRole}
                    className="flex items-center px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => { onDeleteRole(selectedRole) }}
                    className="flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <TrashIcon className="w-4 h-4 mr-1" />
                    Delete
                  </button>
                </div>
              </div>

              {/* Role Properties */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Properties</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Role Name
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.role_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Can Login
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.with_login ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Superuser
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.superuser ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Create DB
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.createDatabase ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Create Role
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.createRole ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Inherit
                    </label>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {selectedRole.inherit ? 'Yes' : 'No'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Role Grants */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Privileges & Grants
                </h3>
                {!roleGrants || !Array.isArray(roleGrants) || roleGrants.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No privileges granted</p>
                ) : (
                  <div className="space-y-2">
                    {roleGrants.map((grant, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {grant.privilege}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            on {grant.object_type} {grant.object_type === 'table' && grant.schema_name ? `${grant.schema_name}.` : ''}{grant.object_name}
                          </span>
                        </div>
                        {grant.grant_option && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            WITH GRANT OPTION
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Privileges Tab Component
interface PrivilegesTabProps {
  users: DatabaseUser[]
  roles: Role[]
  selectedUser: DatabaseUser | null
  selectedRole: Role | null
  userGrants: Grant[]
  roleGrants: Grant[]
  isLoadingUserGrants: boolean
  isLoadingRoleGrants: boolean
  onGrantPrivileges: () => void
  onRevokePrivileges: () => void
  onUserSelect: (user: DatabaseUser | null) => void
  onRoleSelect: (role: Role | null) => void
  isPostgreSQL: boolean
  connectionId: string
  databaseName: string
}

function PrivilegesTab({
  users,
  roles,
  selectedUser,
  selectedRole,
  userGrants,
  roleGrants,
  isLoadingUserGrants,
  isLoadingRoleGrants,
  onGrantPrivileges,
  onRevokePrivileges,
  onUserSelect,
  onRoleSelect,
  isPostgreSQL,
  connectionId,
  databaseName,
}: PrivilegesTabProps) {
  const [defaultPrivileges, setDefaultPrivileges] = useState<{ object_type?: string; object_name?: string; privileges?: string[] }[]>([])
  const [isLoadingDefaults, setIsLoadingDefaults] = useState(false)
  const [sidebarTab, setSidebarTab] = useState<'users' | 'roles'>('users')

  // Load default privileges (PostgreSQL only)
  useEffect(() => {
    if (isPostgreSQL && connectionId && databaseName) {
      setIsLoadingDefaults(true)
      void AgentProxyService.getDefaultPrivileges(connectionId, databaseName)
        .then((response: unknown) => {
          const defaults = (response && typeof response === 'object' && 'default_privileges' in (response as Record<string, unknown>))
            ? (response as { default_privileges?: { object_type?: string; object_name?: string; privileges?: string[] }[] }).default_privileges ?? []
            : []
          setDefaultPrivileges(defaults)
          return
        })
        .catch(() => {
          console.error('Failed to load default privileges')
        })
        .finally(() => {
          setIsLoadingDefaults(false)
        })
    }
  }, [isPostgreSQL, connectionId, databaseName])

  return (
    <div className="flex h-full min-h-0">
      {/* Left Sidebar */}
      <div className="w-96 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 min-h-0">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Select User/Role
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Choose an entity to manage privileges
          </p>
        </div>

        {/* Sidebar Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => { setSidebarTab('users') }}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${
              sidebarTab === 'users'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <div className="flex items-center justify-center space-x-1">
              <UserIcon className="w-4 h-4" />
              <span>Users</span>
              <span className="text-xs bg-gray-200 dark:bg-gray-600 rounded-full px-2 py-0.5">
                {users.length}
              </span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setSidebarTab('roles') }}
            className={`flex-1 px-4 py-2 text-sm font-medium border-b-2 ${
              sidebarTab === 'roles'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            disabled={!isPostgreSQL}
          >
            <div className="flex items-center justify-center space-x-1">
              <UserGroupIcon className="w-4 h-4" />
              <span>Roles</span>
              <span className="text-xs bg-gray-200 dark:bg-gray-600 rounded-full px-2 py-0.5">
                {roles.length}
              </span>
            </div>
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="flex-1 overflow-y-auto">
          {sidebarTab === 'users' ? (
            <div className="p-2">
              {users.length === 0 ? (
                <div className="text-center py-8">
                  <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No users found</p>
                </div>
              ) : (
                users.map((user) => (
                  <button
                    type="button"
                    key={user.user_name}
                    onClick={() => {
                      onUserSelect(user)
                      onRoleSelect(null)
                    }}
                    className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${
                      selectedUser?.user_name === user.user_name
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 ring-1 ring-primary-200 dark:ring-primary-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <UserIcon className="w-5 h-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.user_name}
                        </div>
                        {user.host && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            @{user.host}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="p-2">
              {!isPostgreSQL ? (
                <div className="text-center py-8">
                  <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Roles are only available for PostgreSQL
                  </p>
                </div>
              ) : roles.length === 0 ? (
                <div className="text-center py-8">
                  <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No roles found</p>
                </div>
              ) : (
                roles.map((role) => (
                  <button
                    type="button"
                    key={role.role_name}
                    onClick={() => {
                      onRoleSelect(role)
                      onUserSelect(null)
                    }}
                    className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${
                      selectedRole?.role_name === role.role_name
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 ring-1 ring-primary-200 dark:ring-primary-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <UserGroupIcon className="w-5 h-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {role.role_name}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        {!selectedUser && !selectedRole ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <ShieldCheckIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No User or Role Selected
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Select a user or role from the sidebar to manage privileges
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Privilege Management
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Managing privileges for {selectedUser ? `User: ${selectedUser.user_name}` : `Role: ${selectedRole?.role_name}`}
              </p>
            </div>

            {/* Grant/Revoke Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Privilege Actions
              </h3>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={onGrantPrivileges}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <ShieldCheckIcon className="w-4 h-4 mr-2" />
                  Grant Privileges
                </button>
                <button
                  type="button"
                  onClick={onRevokePrivileges}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={(selectedUser ? userGrants.length === 0 : roleGrants.length === 0)}
                >
                  <LockClosedIcon className="w-4 h-4 mr-2" />
                  Revoke Privileges
                </button>
              </div>
            </div>

            {/* Current Privileges */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Current Privileges for {selectedUser ? `User: ${selectedUser.user_name}` : `Role: ${selectedRole?.role_name}`}
              </h3>
              {(selectedUser ? isLoadingUserGrants : isLoadingRoleGrants) ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading privileges...</span>
                </div>
              ) : (selectedUser ? userGrants : roleGrants).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No privileges granted</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Privilege
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Object Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Object Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Schema
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Grant Option
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(selectedUser ? userGrants : roleGrants).map((grant, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {grant.privilege}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {grant.object_type}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {grant.object_name}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {grant.schema_name || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {grant.grant_option ? (
                              <CheckIcon className="w-4 h-4 text-green-600" />
                            ) : (
                              <XMarkIcon className="w-4 h-4 text-gray-400" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Default Privileges (PostgreSQL only) */}
            {isPostgreSQL && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Default Privileges
                </h3>
                {isLoadingDefaults ? (
                  <div className="flex items-center justify-center p-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  </div>
                ) : defaultPrivileges.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No default privileges configured</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Grantor
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Grantee
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Object Type
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Privileges
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {defaultPrivileges.map((priv: { grantor?: string; grantee?: string; object_type?: string; object_name?: string; privileges?: string[] }, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {priv.grantor}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {priv.grantee}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {priv.object_type}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                              {Array.isArray(priv.privileges) ? priv.privileges.join(', ') : priv.privileges}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Modal Components
interface CreateUserModalProps {
  connectionId: string
  databaseName: string
  isMySQL: boolean
  isPostgreSQL: boolean
  onClose: () => void
  onSuccess: () => void
}

function CreateUserModal({
  connectionId,
  databaseName,
  isMySQL,
  isPostgreSQL,
  onClose,
  onSuccess,
}: CreateUserModalProps) {
  const { showToast } = useToast()
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    host: isMySQL ? '%' : '',
    canLogin: true,
    superuser: false,
    createDatabase: false,
    createRole: false,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.username || !formData.password) {
      showToast('error', 'Username and password are required')
      return
    }

    const userData = {
      username: formData.username,
      password: formData.password,
      host: isMySQL ? formData.host : undefined,
      can_login: formData.canLogin,
      superuser: formData.superuser,
      create_database: isPostgreSQL ? formData.createDatabase : undefined,
      create_role: isPostgreSQL ? formData.createRole : undefined,
    }
    
    console.log('👤 Creating user with data:', userData)
    console.log('👤 Connection ID:', connectionId)
    console.log('👤 Database name:', databaseName)
    
    setIsLoading(true)
    try {
      const response = await AgentProxyService.createUser(connectionId, databaseName, userData)
      console.log('✅ User creation response:', response)
      showToast('success', 'User created successfully')
      onSuccess()
    } catch (error) {
      console.error('❌ User creation error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', `Failed to create user: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Create Database User
            </h2>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => { setFormData({ ...formData, username: e.target.value }) }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => { setFormData({ ...formData, password: e.target.value }) }}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
              <button
                type="button"
                onClick={() => { setShowPassword(!showPassword) }}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {isMySQL && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Host
              </label>
              <input
                type="text"
                value={formData.host}
                onChange={(e) => { setFormData({ ...formData, host: e.target.value }) }}
                placeholder="% for any host"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.canLogin}
                onChange={(e) => { setFormData({ ...formData, canLogin: e.target.checked }) }}
                className="w-4 h-4 text-primary-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can Login</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.superuser}
                onChange={(e) => { setFormData({ ...formData, superuser: e.target.checked }) }}
                className="w-4 h-4 text-primary-600 rounded border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Superuser</span>
            </label>

            {isPostgreSQL && (
              <>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.createDatabase}
                    onChange={(e) => { setFormData({ ...formData, createDatabase: e.target.checked }) }}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Create Database</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.createRole}
                    onChange={(e) => { setFormData({ ...formData, createRole: e.target.checked }) }}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Create Role</span>
                </label>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit User Modal
interface EditUserModalProps {
  connectionId: string
  databaseName: string
  user: DatabaseUser
  isMySQL: boolean
  isPostgreSQL: boolean
  onClose: () => void
  onSuccess: () => void
}

function EditUserModal({ connectionId, databaseName, user, isPostgreSQL, onClose, onSuccess }: EditUserModalProps) {
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    createDatabase: user.createDatabase || false,
    createRole: user.createRole || false,
    superuser: user.superuser || false,
    replication: user.replication || false,
    bypassRls: user.bypassRls || false,
    connectionLimit: (user.connectionLimit ?? -1),
  })
  
  // Sync form with incoming user prop updates
  useEffect(() => {
    setFormData({
      createDatabase: user.createDatabase || false,
      createRole: user.createRole || false,
      superuser: user.superuser || false,
      replication: user.replication || false,
      bypassRls: user.bypassRls || false,
      connectionLimit: (user.connectionLimit ?? -1),
    })
  }, [user])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user.user_name) return
    
    setIsLoading(true)
    try {
      await AgentProxyService.alterUser(connectionId, databaseName, user.user_name, formData)
      showToast('success', `User "${user.user_name}" updated successfully`)
      onSuccess()
    } catch (error) {
      showToast('error', 'Failed to update user', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Edit User: {user.user_name}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {isPostgreSQL && (
              <>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.createDatabase}
                    onChange={(e) => { setFormData({ ...formData, createDatabase: e.target.checked }) }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create databases</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.createRole}
                    onChange={(e) => { setFormData({ ...formData, createRole: e.target.checked }) }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create roles</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.bypassRls}
                    onChange={(e) => { setFormData({ ...formData, bypassRls: e.target.checked }) }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bypass RLS</span>
                </label>
              </>
            )}
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.superuser}
                onChange={(e) => { setFormData({ ...formData, superuser: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Superuser</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.replication}
                onChange={(e) => { setFormData({ ...formData, replication: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Replication</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Connection Limit
              </label>
              <input
                type="number"
                value={formData.connectionLimit}
                onChange={(e) => { setFormData({ ...formData, connectionLimit: parseInt(e.target.value) }) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="-1"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Reset Password Modal
interface ResetPasswordModalProps {
  connectionId: string
  databaseName: string
  user: DatabaseUser
  onClose: () => void
  onSuccess: () => void
}

function ResetPasswordModal({ connectionId, databaseName, user, onClose, onSuccess }: ResetPasswordModalProps) {
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [copied, setCopied] = useState(false)
  
  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?'
    let newPassword = ''
    for (let i = 0; i < 16; i++) {
      newPassword += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setPassword(newPassword)
    setConfirmPassword(newPassword)
  }
  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => { setCopied(false) }, 2000)
      showToast('success', 'Password copied to clipboard')
    } catch {
      showToast('error', 'Failed to copy password')
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!password) {
      showToast('error', 'Password is required')
      return
    }
    
    if (password !== confirmPassword) {
      showToast('error', 'Passwords do not match')
      return
    }
    
    if (password.length < 8) {
      showToast('error', 'Password must be at least 8 characters')
      return
    }
    
    if (!user.user_name) return
    
    setIsLoading(true)
    try {
      await AgentProxyService.alterUser(connectionId, databaseName, user.user_name, { password })
      showToast('success', `Password reset for user "${user.user_name}"`)
      onSuccess()
    } catch (error) {
      showToast('error', 'Failed to reset password', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Reset Password: {user.user_name}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value) }}
                  className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                  minLength={8}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Generate password"
                  >
                    <KeyIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Copy password"
                  >
                    {copied ? <CheckIcon className="w-4 h-4 text-green-500" /> : <ClipboardDocumentIcon className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPassword(!showPassword) }}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                minLength={8}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Create Role Modal (PostgreSQL only)
interface CreateRoleModalProps {
  connectionId: string
  databaseName: string
  onClose: () => void
  onSuccess: () => void
}

function CreateRoleModal({ connectionId, databaseName, onClose, onSuccess }: CreateRoleModalProps) {
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    role_name: '',
    with_login: false,
    inherit: true,
    createDatabase: false,
    createRole: false,
    superuser: false,
    replication: false,
    bypassRls: false,
    connectionLimit: -1,
  })
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.role_name.trim()) {
      showToast('error', 'Role name is required')
      return
    }
    
    console.log('📝 Submitting role creation form:', formData)
    console.log('📝 Connection ID:', connectionId)
    console.log('📝 Database name:', databaseName)
    
    setIsLoading(true)
    try {
      const response = await AgentProxyService.createRole(connectionId, databaseName, formData)
      console.log('✅ Role creation response:', response)
      showToast('success', `Role "${formData.role_name}" created successfully`)
      onSuccess()
    } catch (error) {
      console.error('❌ Role creation error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', `Failed to create role: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create New Role
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.role_name}
                onChange={(e) => { setFormData({ ...formData, role_name: e.target.value }) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.with_login}
                onChange={(e) => { setFormData({ ...formData, with_login: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can login</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.inherit}
                onChange={(e) => { setFormData({ ...formData, inherit: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Inherit privileges</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.createDatabase}
                onChange={(e) => { setFormData({ ...formData, createDatabase: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create databases</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.createRole}
                onChange={(e) => { setFormData({ ...formData, createRole: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create roles</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.superuser}
                onChange={(e) => { setFormData({ ...formData, superuser: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Superuser</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.replication}
                onChange={(e) => { setFormData({ ...formData, replication: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Replication</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.bypassRls}
                onChange={(e) => { setFormData({ ...formData, bypassRls: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bypass RLS</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Connection Limit
              </label>
              <input
                type="number"
                value={formData.connectionLimit}
                onChange={(e) => { setFormData({ ...formData, connectionLimit: parseInt(e.target.value) }) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="-1"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Role Modal
interface EditRoleModalProps {
  connectionId: string
  databaseName: string
  role: Role
  onClose: () => void
  onSuccess: () => void
}

function EditRoleModal({ connectionId, databaseName, role, onClose, onSuccess }: EditRoleModalProps) {
  const { showToast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    with_login: role.with_login || false,
    inherit: role.inherit || false,
    createDatabase: role.createDatabase || false,
    createRole: role.createRole || false,
    superuser: role.superuser || false,
    replication: role.replication || false,
    bypassRls: role.bypassRls || false,
    connectionLimit: (role.connectionLimit ?? -1),
  })
  
  // Sync form with incoming role prop updates
  useEffect(() => {
    setFormData({
      with_login: role.with_login || false,
      inherit: role.inherit || false,
      createDatabase: role.createDatabase || false,
      createRole: role.createRole || false,
      superuser: role.superuser || false,
      replication: role.replication || false,
      bypassRls: role.bypassRls || false,
      connectionLimit: (role.connectionLimit ?? -1),
    })
  }, [role])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    setIsLoading(true)
    try {
      await AgentProxyService.alterRole(connectionId, databaseName, role.role_name, formData)
      showToast('success', `Role "${role.role_name}" updated successfully`)
      onSuccess()
    } catch (error) {
      showToast('error', 'Failed to update role', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Edit Role: {role.role_name}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.with_login}
                onChange={(e) => { setFormData({ ...formData, with_login: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can login</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.inherit}
                onChange={(e) => { setFormData({ ...formData, inherit: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Inherit privileges</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.createDatabase}
                onChange={(e) => { setFormData({ ...formData, createDatabase: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create databases</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.createRole}
                onChange={(e) => { setFormData({ ...formData, createRole: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Can create roles</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.superuser}
                onChange={(e) => { setFormData({ ...formData, superuser: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Superuser</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.replication}
                onChange={(e) => { setFormData({ ...formData, replication: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Replication</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.bypassRls}
                onChange={(e) => { setFormData({ ...formData, bypassRls: e.target.checked }) }}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Bypass RLS</span>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Connection Limit
              </label>
              <input
                type="number"
                value={formData.connectionLimit}
                onChange={(e) => { setFormData({ ...formData, connectionLimit: parseInt(e.target.value) }) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                min="-1"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Updating...' : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Grant Privileges Modal
function GrantPrivilegesModal({ 
  isOpen, 
  onClose, 
  grantee, 
  granteeType, 
  databaseName,
  connectionId,
  isPostgreSQL,
  mysqlHost,
  onSuccess 
}: {
  isOpen: boolean
  onClose: () => void
  grantee: string | null
  granteeType: 'user' | 'role'
  databaseName: string
  connectionId: string
  isPostgreSQL: boolean
  mysqlHost?: string
  onSuccess: () => void
}) {
  const { showToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [privileges, setPrivileges] = useState<string[]>([])
  const [objectType, setObjectType] = useState<'global' | 'database' | 'table' | 'schema'>('database')
  const [objectName, setObjectName] = useState(databaseName)
  const [schemaName, setSchemaName] = useState('public')
  const [withGrantOption, setWithGrantOption] = useState(false)
  const [schemas, setSchemas] = useState<string[]>([])
  const [tables, setTables] = useState<string[]>([])
  const [databases, setDatabases] = useState<string[]>([])
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false)
  const [isLoadingTables, setIsLoadingTables] = useState(false)
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false)
  
  // Available privileges based on database type and object type
  const availablePrivileges = useMemo(() => {
    if (isPostgreSQL) {
      switch (objectType) {
        case 'database':
          return ['CONNECT', 'CREATE', 'TEMPORARY', 'TEMP']
        case 'schema':
          return ['CREATE', 'USAGE']
        case 'table':
          return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'TRUNCATE', 'REFERENCES', 'TRIGGER']
        case 'global':
          return ['CONNECT', 'CREATE', 'TEMPORARY', 'TEMP']
      }
    } else {
      // MySQL
      switch (objectType) {
        case 'global':
          return [
            'ALL PRIVILEGES',
            'SHOW DATABASES',
            'SELECT',
            'INSERT',
            'UPDATE',
            'DELETE',
            'CREATE',
            'DROP',
            'ALTER',
            'INDEX',
            'REFERENCES',
            'PROCESS',
            'RELOAD',
            'SHUTDOWN',
            'FILE',
            'CREATE USER',
            'EVENT',
            'TRIGGER',
            'EXECUTE',
            'REPLICATION CLIENT'
          ]
        case 'database':
          return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'INDEX', 'REFERENCES']
        case 'schema':
          return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'INDEX', 'REFERENCES']
        case 'table':
          return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'INDEX', 'REFERENCES']
        default:
          return ['SELECT', 'INSERT', 'UPDATE', 'DELETE', 'CREATE', 'DROP', 'ALTER', 'INDEX', 'REFERENCES']
      }
    }
  }, [isPostgreSQL, objectType])
  
  const handlePrivilegeToggle = (privilege: string) => {
    setPrivileges(prev => 
      prev.includes(privilege) 
        ? prev.filter(p => p !== privilege)
        : [...prev, privilege]
    )
  }
  
  // Load databases when modal opens
  useEffect(() => {
    const loadDatabases = async () => {
      if (!isOpen || !connectionId) return
      
      setIsLoadingDatabases(true)
      try {
        const response = await AgentProxyService.getDatabases(connectionId)
        console.log('Databases response:', response)
        
        let databasesList: string[] = []
        if (response && typeof response === 'object') {
          if ('databases' in response) {
            databasesList = (response as { databases?: { database_name?: string; name?: string }[] }).databases?.map((db) => db.database_name || db.name || '') || []
          } else if ('result' in response && (response as { result?: { databases?: { database_name?: string; name?: string }[] } }).result?.databases) {
            databasesList = (response as { result?: { databases?: { database_name?: string; name?: string }[] } }).result!.databases!.map((db) => db.database_name || db.name || '') || []
          } else if (Array.isArray(response)) {
            databasesList = response
              .map((db: unknown) => {
                if (typeof db === 'string') return db
                if (db && typeof db === 'object') {
                  const maybe = db as { database_name?: unknown; name?: unknown }
                  const val = (typeof maybe.database_name === 'string' ? maybe.database_name : undefined)
                    ?? (typeof maybe.name === 'string' ? maybe.name : undefined)
                  return val ?? ''
                }
                return ''
              })
              .filter((s): s is string => typeof s === 'string' && s.length > 0)
          }
        }
        
        setDatabases(databasesList)
        // Set current database as default if available in the list
        if (databasesList.length > 0 && !databasesList.includes(objectName) && objectType === 'database') {
          if (databasesList.includes(databaseName)) {
            setObjectName(databaseName)
          } else {
            setObjectName(databasesList[0])
          }
        }
      } catch (error) {
        console.error('Failed to load databases:', error)
        showToast('error', 'Failed to load databases')
      } finally {
        setIsLoadingDatabases(false)
      }
    }
    
    void loadDatabases()
  }, [isOpen, connectionId, objectType, objectName, databaseName, showToast])

  // Load schemas when modal opens (PostgreSQL only)
  useEffect(() => {
    const loadSchemas = async () => {
      if (!isPostgreSQL || !isOpen || !connectionId || !databaseName) return
      
      setIsLoadingSchemas(true)
      try {
        const response = await AgentProxyService.getSchemas(connectionId, databaseName)
        console.log('Schemas response:', response)
        
        let schemasList: string[] = []
        if (response && typeof response === 'object') {
          if ('schemas' in response) {
            schemasList = (response as { schemas?: { schema_name?: string; name?: string }[] }).schemas?.map((s) => s.schema_name || s.name || '') || []
          } else if ('result' in response && (response as { result?: { schemas?: { schema_name?: string; name?: string }[] } }).result?.schemas) {
            schemasList = (response as { result?: { schemas?: { schema_name?: string; name?: string }[] } }).result!.schemas!.map((s) => s.schema_name || s.name || '') || []
          } else if (Array.isArray(response)) {
            schemasList = response
              .map((s: unknown) => {
                if (typeof s === 'string') return s
                if (s && typeof s === 'object') {
                  const maybe = s as { schema_name?: unknown; name?: unknown }
                  const val = (typeof maybe.schema_name === 'string' ? maybe.schema_name : undefined)
                    ?? (typeof maybe.name === 'string' ? maybe.name : undefined)
                  return val ?? ''
                }
                return ''
              })
              .filter((x): x is string => typeof x === 'string' && x.length > 0)
          }
        }
        
        setSchemas(schemasList)
        // Set default schema if available
        if (schemasList.length > 0 && !schemasList.includes(schemaName)) {
          setSchemaName(schemasList.includes('public') ? 'public' : schemasList[0])
        }
      } catch (error) {
        console.error('Failed to load schemas:', error)
        showToast('error', 'Failed to load schemas')
      } finally {
        setIsLoadingSchemas(false)
      }
    }
    
    void loadSchemas()
  }, [isPostgreSQL, isOpen, connectionId, databaseName, schemaName, showToast])
  
  // Load schemas for schema selection when object type is 'schema'
  useEffect(() => {
    const loadSchemasForSelection = async () => {
      if (!isPostgreSQL || objectType !== 'schema' || !connectionId || !databaseName) return
      
      setIsLoadingSchemas(true)
      try {
        const response = await AgentProxyService.getSchemas(connectionId, databaseName)
        console.log('Schemas for selection response:', response)
        
        let schemasList: string[] = []
        if (response && typeof response === 'object') {
          if ('schemas' in response) {
            schemasList = (response as { schemas?: { schema_name?: string; name?: string }[] }).schemas?.map((s) => s.schema_name || s.name || '') || []
          } else if ('result' in response && (response as { result?: { schemas?: { schema_name?: string; name?: string }[] } }).result?.schemas) {
            schemasList = (response as { result?: { schemas?: { schema_name?: string; name?: string }[] } }).result!.schemas!.map((s) => s.schema_name || s.name || '') || []
          } else if (Array.isArray(response)) {
            schemasList = response
              .map((s: unknown) => {
                if (typeof s === 'string') return s
                if (s && typeof s === 'object') {
                  const maybe = s as { schema_name?: unknown; name?: unknown }
                  const val = (typeof maybe.schema_name === 'string' ? maybe.schema_name : undefined)
                    ?? (typeof maybe.name === 'string' ? maybe.name : undefined)
                  return val ?? ''
                }
                return ''
              })
              .filter((x): x is string => typeof x === 'string' && x.length > 0)
          }
        }
        
        setSchemas(schemasList)
        // Set default schema for selection if available
        if (schemasList.length > 0 && !schemasList.includes(objectName)) {
          setObjectName(schemasList.includes('public') ? 'public' : schemasList[0])
        }
      } catch (error) {
        console.error('Failed to load schemas for selection:', error)
        showToast('error', 'Failed to load schemas')
      } finally {
        setIsLoadingSchemas(false)
      }
    }
    
    void loadSchemasForSelection()
  }, [isPostgreSQL, objectType, connectionId, databaseName, objectName, showToast])
  
  // Load tables when object type is 'table' and schema is selected
  useEffect(() => {
    const loadTables = async () => {
      if (objectType !== 'table' || !connectionId || !databaseName || (!isPostgreSQL && !schemaName)) return
      
      setIsLoadingTables(true)
      try {
        const response = await AgentProxyService.getTables(connectionId, databaseName, isPostgreSQL ? schemaName : undefined)
        console.log('Tables response:', response)
        
        let tablesList: string[] = []
        if (response && typeof response === 'object') {
          if ('tables' in response) {
            tablesList = (response as { tables?: { table_name?: string; name?: string }[] }).tables?.map((t) => t.table_name || t.name || '') || []
          } else if ('result' in response && (response as { result?: { tables?: { table_name?: string; name?: string }[] } }).result?.tables) {
            tablesList = (response as { result?: { tables?: { table_name?: string; name?: string }[] } }).result!.tables!.map((t) => t.table_name || t.name || '') || []
          } else if (Array.isArray(response)) {
            tablesList = response
              .map((t: unknown) => {
                if (typeof t === 'string') return t
                if (t && typeof t === 'object') {
                  const maybe = t as { table_name?: unknown; name?: unknown }
                  const val = (typeof maybe.table_name === 'string' ? maybe.table_name : undefined)
                    ?? (typeof maybe.name === 'string' ? maybe.name : undefined)
                  return val ?? ''
                }
                return ''
              })
              .filter((x): x is string => typeof x === 'string' && x.length > 0)
          }
        }
        
        setTables(tablesList)
        // Reset object name when tables change
        setObjectName('')
      } catch (error) {
        console.error('Failed to load tables:', error)
        showToast('error', 'Failed to load tables')
      } finally {
        setIsLoadingTables(false)
      }
    }
    
    void loadTables()
  }, [objectType, connectionId, databaseName, schemaName, isPostgreSQL, showToast])
  
  // Handle object type change
  const handleObjectTypeChange = (newObjectType: 'global' | 'database' | 'table' | 'schema') => {
    setObjectType(newObjectType)
    
    // Reset selected privileges since available privileges change per object type
    setPrivileges([])
    
    // Reset object name and set appropriate default
    if (newObjectType === 'database') {
      setObjectName(databaseName)
    } else if (newObjectType === 'schema') {
      setObjectName(schemaName)
    } else {
      setObjectName('')
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!grantee || privileges.length === 0) {
      showToast('error', 'Please select at least one privilege')
      return
    }
    
    setIsSubmitting(true)
    try {
      // MySQL validation: prevent granting global-only privileges at database/table level
      if (!isPostgreSQL && objectType !== 'global') {
        const globalOnly = new Set(['RELOAD', 'SHUTDOWN', 'PROCESS', 'FILE'])
        const invalid = privileges.find(p => globalOnly.has(p))
        if (invalid) {
          showToast('error', 'Invalid privilege scope', `${invalid} is a global privilege in MySQL and cannot be granted at ${objectType} level.`)
          setIsSubmitting(false)
          return
        }
      }
      // Construct proper object_name based on database type and object type
      let finalObjectName = objectName
      
      // For MySQL table privileges, use database.table format
      if (!isPostgreSQL && objectType === 'table' && objectName) {
        finalObjectName = `${databaseName}.${objectName}`
      }

      // For PostgreSQL table privileges, use schema.table format to ensure proper qualification
      if (isPostgreSQL && objectType === 'table' && objectName && schemaName) {
        finalObjectName = `${schemaName}.${objectName}`
      }
      
      const privilegeData: Record<string, unknown> = {
        privileges,
        object_type: objectType,
        with_grant_option: withGrantOption
      }

      if (objectType !== 'global') {
        privilegeData.object_name = finalObjectName
      } else if (!isPostgreSQL) {
        // MySQL global: include host (defaults to "%")
        privilegeData.host = mysqlHost || '%'
      }
      
      // Add schema information for PostgreSQL table privileges
      if (isPostgreSQL && objectType === 'table' && schemaName) {
        privilegeData.schema_name = schemaName
      }
      
      await AgentProxyService.grantPrivileges(connectionId, databaseName, grantee, privilegeData)
      
      showToast('success', `Privileges granted to ${granteeType} ${grantee}`)
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Grant privileges error:', error)
      showToast('error', 'Failed to grant privileges', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  if (!isOpen || !grantee) return null
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Grant Privileges
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {granteeType === 'user' ? 'User' : 'Role'}
              </label>
              <input
                type="text"
                value={grantee}
                disabled
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Object Type
              </label>
              <select
                value={objectType}
                onChange={(e) => { 
                  const value = e.target.value
                  if (value === 'global' || value === 'database' || value === 'table' || value === 'schema') {
                    handleObjectTypeChange(value)
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {!isPostgreSQL && <option value="global">Global (*.*)</option>}
                <option value="database">Database</option>
                <option value="table">Table</option>
                {isPostgreSQL && <option value="schema">Schema</option>}
              </select>
            </div>
            
            {/* Schema Selection (PostgreSQL only, for table privileges) */}
            {isPostgreSQL && objectType === 'table' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Schema
                </label>
                <select
                  value={schemaName}
                  onChange={(e) => { setSchemaName(e.target.value) }}
                  disabled={isLoadingSchemas}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option value="">Select schema...</option>
                  {schemas.map((schema) => (
                    <option key={schema} value={schema}>
                      {schema}
                    </option>
                  ))}
                </select>
                {isLoadingSchemas && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Loading schemas...</p>
                )}
              </div>
            )}
            
            {objectType !== 'global' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {objectType === 'database' ? 'Database Name' : 
                 objectType === 'schema' ? 'Schema Name' : 'Table Name'}
              </label>
              {objectType === 'table' ? (
                <select
                  value={objectName}
                  onChange={(e) => { setObjectName(e.target.value) }}
                  disabled={isLoadingTables || (isPostgreSQL && !schemaName)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option value="">Select table...</option>
                  {tables.map((table) => (
                    <option key={table} value={table}>
                      {table}
                    </option>
                  ))}
                </select>
              ) : objectType === 'database' ? (
                <select
                  value={objectName}
                  onChange={(e) => { setObjectName(e.target.value) }}
                  disabled={isLoadingDatabases}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option value="">Select database...</option>
                  {databases.map((database) => (
                    <option key={database} value={database}>
                      {database}
                    </option>
                  ))}
                </select>
              ) : objectType === 'schema' && isPostgreSQL ? (
                <select
                  value={objectName}
                  onChange={(e) => { setObjectName(e.target.value) }}
                  disabled={isLoadingSchemas}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                >
                  <option value="">Select schema...</option>
                  {schemas.map((schema) => (
                    <option key={schema} value={schema}>
                      {schema}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={objectName}
                  onChange={(e) => { setObjectName(e.target.value) }}
                  placeholder={`Enter ${objectType} name`}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              )}
              {objectType === 'table' && isLoadingTables && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Loading tables...</p>
              )}
              {objectType === 'table' && isPostgreSQL && !schemaName && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Please select a schema first</p>
              )}
              {objectType === 'database' && isLoadingDatabases && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Loading databases...</p>
              )}
              {objectType === 'schema' && isPostgreSQL && isLoadingSchemas && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Loading schemas...</p>
              )}
            </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Privileges
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-2">
                {availablePrivileges.map((privilege) => (
                  <label key={privilege} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={privileges.includes(privilege)}
                      onChange={() => { handlePrivilegeToggle(privilege) }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-white">
                      {privilege}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {(isPostgreSQL || (!isPostgreSQL && objectType === 'global')) && (
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={withGrantOption}
                    onChange={(e) => { setWithGrantOption(e.target.checked) }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-900 dark:text-white">
                    With Grant Option (allows grantee to grant these privileges to others)
                  </span>
                </label>
              </div>
            )}
          </div>
          
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || privileges.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
            >
              {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Grant Privileges
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Revoke Privileges Modal 
function RevokePrivilegesModal({ 
  isOpen, 
  onClose, 
  grantee, 
  granteeType, 
  currentGrants,
  databaseName,
  connectionId,
  isPostgreSQL,
  mysqlHost,
  onSuccess 
}: {
  isOpen: boolean
  onClose: () => void
  grantee: string | null
  granteeType: 'user' | 'role'
  currentGrants: Grant[]
  databaseName: string
  connectionId: string
  isPostgreSQL?: boolean
  mysqlHost?: string
  onSuccess: () => void
}) {
  const { showToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedGrants, setSelectedGrants] = useState<string[]>([])
  
  // Helper to decide if a grant is inherited based on its source label
  const isGrantInherited = (source?: string): boolean => {
    if (!source) return false
    const normalized = source.trim().toLowerCase()
    // Treat these markers as directly granted (revocable from the user)
    if (normalized === 'direct' || normalized === 'manual' || normalized === 'explicit') {
      return false
    }
    return true
  }

  // Build a stable unique key for a grant (used for de-dup and selection)
  const makeKey = useCallback((g: Grant): string => {
    const effectiveName = g.object_type === 'database'
      ? (g.object_name && g.object_name !== '' ? g.object_name : databaseName)
      : g.object_name
    return `${g.privilege}_${g.object_type}_${g.schema_name || ''}_${effectiveName}`
  }, [databaseName])

  // De-duplicate identical grants coming from multiple sources
  const dedupedGrants = useMemo(() => {
    const map = new Map<string, Grant>()
    for (const g of currentGrants) {
      const key = makeKey(g)
      if (!map.has(key)) map.set(key, g)
    }
    return Array.from(map.values())
  }, [currentGrants, makeKey])
  
  // Sort so revocable (non-inherited) grants are shown first
  const sortedGrants = useMemo(() => {
    const grants = [...dedupedGrants]
    grants.sort((a, b) => {
      const aInherited = isGrantInherited(a.source) ? 1 : 0
      const bInherited = isGrantInherited(b.source) ? 1 : 0
      if (aInherited !== bInherited) return aInherited - bInherited
      // Stable secondary sort for predictability
      const nameA = a.object_type === 'database'
        ? (a.object_name && a.object_name !== '' ? a.object_name : databaseName)
        : a.object_name
      const nameB = b.object_type === 'database'
        ? (b.object_name && b.object_name !== '' ? b.object_name : databaseName)
        : b.object_name
      return (
        a.privilege.localeCompare(b.privilege) ||
        a.object_type.localeCompare(b.object_type) ||
        (nameA || '').localeCompare(nameB || '')
      )
    })
    return grants
  }, [dedupedGrants, databaseName])
  
  const handleGrantToggle = (grantKey: string) => {
    setSelectedGrants(prev => 
      prev.includes(grantKey) 
        ? prev.filter(g => g !== grantKey)
        : [...prev, grantKey]
    )
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!grantee || selectedGrants.length === 0) {
      showToast('error', 'Please select at least one grant to revoke')
      return
    }
    
    // Filter out inherited privileges that cannot be revoked directly
    const validGrantsToRevoke = selectedGrants.filter(grantKey => {
      const grant = dedupedGrants.find(g => makeKey(g) === grantKey)
      return !isGrantInherited(grant?.source)
    })
    
    if (validGrantsToRevoke.length === 0) {
      showToast('error', 'Cannot revoke inherited privileges', 'All selected privileges are inherited from roles and cannot be revoked directly from the user.')
      return
    }
    
    if (validGrantsToRevoke.length !== selectedGrants.length) {
      const skippedCount = selectedGrants.length - validGrantsToRevoke.length
      showToast('warning', `Skipping ${skippedCount} inherited privilege(s)`, `${skippedCount} inherited privileges will be skipped as they cannot be revoked directly.`)
    }
    
    setIsSubmitting(true)
    try {
      // Group grants by object and revoke them (only process valid, non-inherited grants)
      for (const grantKey of validGrantsToRevoke) {
        const grant = dedupedGrants.find(g => makeKey(g) === grantKey)
        
        if (grant) {
          let effectiveObjectName = grant.object_type === 'database'
            ? (grant.object_name && grant.object_name !== '' ? grant.object_name : databaseName)
            : grant.object_name
          // Ensure schema-qualified table for PostgreSQL
          if ((isPostgreSQL ?? false) && grant.object_type === 'table' && grant.schema_name && grant.object_name && !grant.object_name.includes('.')) {
            effectiveObjectName = `${grant.schema_name}.${grant.object_name}`
          }
          const revokePayload: Record<string, unknown> = {
            privileges: [grant.privilege],
            object_type: grant.object_type
          }
          if (grant.object_type !== 'global') {
            revokePayload.object_name = effectiveObjectName
          } else if (!isPostgreSQL) {
            revokePayload.host = mysqlHost || '%'
          }
          if (grant.object_type === 'table' && grant.schema_name) {
            revokePayload.schema_name = grant.schema_name
          }
          await AgentProxyService.revokePrivileges(connectionId, databaseName, grantee, revokePayload)
        }
      }
      
      showToast('success', `Privileges revoked from ${granteeType} ${grantee}`)
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Revoke privileges error:', error)
      
      // Check if this is an inherited privilege error
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const isInheritedPrivilegeError = errorMessage.includes('inherited from a role') || 
                                       errorMessage.includes('cannot be revoked directly')
      
      if (isInheritedPrivilegeError) {
        // Extract privilege name and role from error message if possible
        const privilegeMatch = errorMessage.match(/Cannot revoke (\w+) privilege/)
        const privilegeName = privilegeMatch ? privilegeMatch[1] : 'privilege'
        
        const roleMatch = errorMessage.match(/role \(like (\w+)\)/)
        const roleName = roleMatch ? roleMatch[1] : 'a role'
        
        showToast('error', 'Cannot Revoke Inherited Privilege', 
          `The ${privilegeName} privilege is inherited from ${roleName} and cannot be revoked directly from the user. To remove this privilege, you must revoke it from the ${roleName} role instead.`)
      } else {
        showToast('error', 'Failed to revoke privileges', errorMessage)
      }
    } finally {
      setIsSubmitting(false)
    }
  }
  
  if (!isOpen || !grantee) return null
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Revoke Privileges
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {granteeType === 'user' ? 'User' : 'Role'}
              </label>
              <input
                type="text"
                value={grantee}
                disabled
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Current Grants (select to revoke)
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Inherited privileges (marked with orange badge) cannot be revoked directly and are disabled. 
                To remove inherited privileges, revoke them from the source role.
              </p>
              {sortedGrants.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No privileges granted
                </p>
              ) : (
                <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-3 space-y-2">
                  {sortedGrants.map((grant, idx) => {
                    const effectiveObjectName = grant.object_type === 'database'
                      ? (grant.object_name && grant.object_name !== '' ? grant.object_name : databaseName)
                      : grant.object_name
                    const baseKey = makeKey(grant)
                    const reactKey = `${baseKey}_${grant.source || 'none'}_${idx}`
                    const isInherited = isGrantInherited(grant.source)
                    const isDisabled = isInherited
                    
                    return (
                      <label key={reactKey} className={`flex items-center ${isDisabled ? 'opacity-60' : ''}`}>
                        <input
                          type="checkbox"
                          checked={selectedGrants.includes(baseKey)}
                          onChange={() => { handleGrantToggle(baseKey) }}
                          disabled={isDisabled}
                          className="rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
                          title={isInherited ? `This privilege is inherited from ${grant.source} and cannot be revoked directly` : ''}
                        />
                        <div className="ml-2 flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {grant.privilege}
                            </div>
                            {isInherited && (
                              <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full">
                                Inherited
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {grant.object_type}: {effectiveObjectName}
                            {grant.grant_option && (
                              <span className="ml-1 text-blue-600 dark:text-blue-400">(with grant option)</span>
                            )}
                            {grant.source && (
                              <span className="ml-1 text-gray-400 dark:text-gray-500">
                                • from {grant.source}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || selectedGrants.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Revoke Privileges
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Remove Membership Modal Component
interface RemoveMembershipModalProps {
  isOpen: boolean
  onClose: () => void
  username: string
  memberRoles: string[]
  onConfirm: (username: string, roleName: string) => Promise<void>
}

function RemoveMembershipModal({
  isOpen,
  onClose,
  username,
  memberRoles,
  onConfirm,
}: RemoveMembershipModalProps) {
  const [selectedRole, setSelectedRole] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRole) return

    // Prevent removal of PUBLIC membership
    if (selectedRole === 'PUBLIC') {
      setError('PUBLIC is a special PostgreSQL keyword and cannot be removed as a membership')
      return
    }

    // Validation
    if (!memberRoles.includes(selectedRole)) {
      setError('Selected role is not a valid membership for this user')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await onConfirm(username, selectedRole)
      onClose()
      setSelectedRole('')
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove membership')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedRole('')
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Remove User Membership
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Remove {username} from a role membership
            </p>
          </div>
          
          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Role to Remove
              </label>
              {memberRoles.includes('PUBLIC') && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Note: PUBLIC is a special PostgreSQL keyword representing all users and cannot be removed.
                </p>
              )}
              <select
                value={selectedRole}
                onChange={(e) => { setSelectedRole(e.target.value) }}
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50"
              >
                <option value="">Choose a role...</option>
                {memberRoles
                  .filter(role => role !== 'PUBLIC')
                  .map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
              </select>
              {memberRoles.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This user is not a member of any roles
                </p>
              )}
              {memberRoles.length > 0 && memberRoles.filter(role => role !== 'PUBLIC').length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This user only has PUBLIC membership, which cannot be removed
                </p>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedRole || memberRoles.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center"
            >
              {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Remove Membership
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Reassign Owned Objects Modal Component
interface ReassignOwnedObjectsModalProps {
  isOpen: boolean
  onClose: () => void
  fromUsername: string
  availableUsers: string[]
  onConfirm: (fromUsername: string, toUsername: string) => Promise<void>
}

function ReassignOwnedObjectsModal({
  isOpen,
  onClose,
  fromUsername,
  availableUsers,
  onConfirm,
}: ReassignOwnedObjectsModalProps) {
  const [selectedUser, setSelectedUser] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUser) return

    // Validation
    if (!availableUsers.includes(selectedUser)) {
      setError('Selected user is not available for reassignment')
      return
    }

    if (selectedUser === fromUsername) {
      setError('Cannot reassign objects to the same user')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await onConfirm(fromUsername, selectedUser)
      onClose()
      setSelectedUser('')
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reassign owned objects')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedUser('')
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Reassign Owned Objects
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Reassign all objects owned by {fromUsername} to another user
            </p>
          </div>
          
          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Target User
              </label>
              <select
                value={selectedUser}
                onChange={(e) => { setSelectedUser(e.target.value) }}
                required
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-purple-500 focus:border-purple-500 disabled:opacity-50"
              >
                <option value="">Choose a user...</option>
                {availableUsers.map((user) => (
                  <option key={user} value={user}>
                    {user}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This will reassign all schemas, tables, views, sequences, and other objects owned by {fromUsername}
              </p>
              {availableUsers.length === 0 && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                  No other users available for reassignment
                </p>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 rounded-b-lg flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedUser || availableUsers.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center"
            >
              {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              Reassign Objects
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}